import React, { useState, useEffect, useCallback } from 'react';
import { useNews } from '../context/NewsContext';
import { useContent } from '../context/ContentContext';
import { useHero } from '../context/HeroContext';
import { useLegal } from '../context/LegalContext';
import { useLinks } from '../context/LinksContext';
import { useImpact } from '../context/ImpactContext';
import { useDevelopmentEngines, defaultDevelopmentEngines } from '../context/DevelopmentEnginesContext';
import RichTextEditor from '../components/RichTextEditor';
import ImageUpload from '../components/ImageUpload';
import MultiImageUpload from '../components/MultiImageUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import LayersIcon from '@mui/icons-material/Layers';
import StarIcon from '@mui/icons-material/Star';
import ScienceIcon from '@mui/icons-material/Science';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GavelIcon from '@mui/icons-material/Gavel';
import ShieldIcon from '@mui/icons-material/Shield';
import EngineeringIcon from '@mui/icons-material/Engineering';
import BuildIcon from '@mui/icons-material/Build';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Toast from '../components/Toast';
import TrainingManagement from './TrainingManagement';
import {
    DndContext,
    closestCenter,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    SortableContext,
    rectSortingStrategy,
    verticalListSortingStrategy,
    arrayMove
} from '@dnd-kit/sortable';
import { SortableItem } from '../components/SortableItem';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Paper,
    Grid,
    TextField,
    Button,
    FormControlLabel,
    Checkbox,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    Divider,
    Chip,
    IconButton,
    Container,
    CircularProgress,
    Stack,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TablePagination,
    useMediaQuery
} from '@mui/material';
import packageJson from '../../package.json';
import {
    getInternalAnalyticsOverview,
    getSeoHealthOverview,
    getContactRoutingConfig,
    updateContactRoutingConfig
} from '../services/api';

const AdminPanel = () => {
    // Toast State
    const [toast, setToast] = useState(null);
    // Must be declared before any effects that reference it (dependency arrays are evaluated immediately).
    const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('modulo') === 'capacitacao' ? 'trainingManagement' : 'news');
    const isMobile = useMediaQuery('(max-width:900px)');
    const isLargeDesktop = useMediaQuery('(min-width:1200px)');
    const [sidebarExpanded, setSidebarExpanded] = useState(true);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const handleCloseToast = (event, reason) => {
        if (reason === 'clickaway') return;
        setToast(null);
    };

    const adminNavItems = [
        { value: 'trainingManagement', label: 'Capacitação', icon: <ShieldIcon /> },
        { value: 'news', label: 'Notícias', icon: <NewspaperIcon /> },
        { value: 'content', label: 'Conteúdos', icon: <LayersIcon /> },
        { value: 'hero', label: 'Destaques', icon: <StarIcon /> },
        { value: 'impact', label: 'Impacto', icon: <ScienceIcon /> },
        { value: 'cases', label: 'Casos', icon: <EmojiEventsIcon /> },
        { value: 'engines', label: 'Motores', icon: <EngineeringIcon /> },
        { value: 'legal', label: 'Legal', icon: <GavelIcon /> },
        { value: 'links', label: 'Serviços', icon: <BuildIcon /> },
        { value: 'executivePanel', label: 'Painel Executivo', icon: <SpaceDashboardIcon /> },
        { value: 'internalBase', label: 'Analytics', icon: <QueryStatsIcon /> },
        { value: 'seoHealth', label: 'SEO Monitor', icon: <TravelExploreIcon /> },
        { value: 'contactRouting', label: 'Contato SMTP', icon: <MailOutlineIcon /> }
    ];

    // Helper to format Date for Input (YYYY-MM-DD)
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        // Check if already YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;

        // Try to parse text like "10 de Dezembro de 2024"
        // This is tricky without a library, so we might just reset if it renders invalid
        // Ideally, we start saving new dates correctly.
        return '';
    };
    // News State
    const { news, addNews, deleteNews, updateNews, reorderNews } = useNews();
    const [editingId, setEditingId] = useState(null);
    const [newsEditorOpen, setNewsEditorOpen] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        subtitle: '',
        image: '',
        imageMetadata: null,
        content: '',
        published: true,
        gallery: [],
        author: '',
        publishedAt: ''
    });

    // Content State
    const { contents, addContent, deleteContent, updateContent } = useContent();
    const [editingContentId, setEditingContentId] = useState(null);
    const [contentFormData, setContentFormData] = useState({
        title: '',
        description: '',
        label: '',
        image: '',
        imageMetadata: null,
        author: '',
        body: ''
    });

    // Hero State
    const { heroData, updateHeroData, addMedia, removeMedia, contentChannel, contentKey, loadedFromKey, usingLegacyFallback } = useHero();
    const [heroFormData, setHeroFormData] = useState({
        title: '',
        summary: '',
        tag: ''
    });
    const [mediaInput, setMediaInput] = useState('');
    const [mediaType, setMediaType] = useState('image');

    // Legal State (Refactored for Database)
    const {
        documents: legalDocuments,
        createDocument,
        updateDocument,
        deleteDocument,
        refreshDocuments
    } = useLegal();

    // selectedDocId can be a number (ID) or 'new'
    const [selectedDocId, setSelectedDocId] = useState(null);
    const [legalForm, setLegalForm] = useState({
        id: null,
        title: '',
        content: '',
        category: 'governance',
        slug: ''
    });

    // Initialize selection when tab opens
    useEffect(() => {
        if (activeTab === 'legal' && legalDocuments.length > 0 && !selectedDocId) {
            // Try to find Terms of Use as default
            const defaultDoc = legalDocuments.find(d => d.slug === 'terms-of-use') || legalDocuments[0];
            if (defaultDoc) setSelectedDocId(defaultDoc.id);
        }
    }, [activeTab, legalDocuments, selectedDocId]);

    // Sync form with selection
    useEffect(() => {
        if (!selectedDocId) return;

        if (selectedDocId === 'new') {
            setLegalForm({
                id: null,
                title: 'Novo Documento',
                content: '',
                category: 'governance',
                slug: ''
            });
            return;
        }

        const doc = legalDocuments.find(d => d.id === selectedDocId);
        if (doc) {
            setLegalForm({
                id: doc.id,
                title: doc.title,
                content: doc.content || '',
                category: doc.category,
                slug: doc.slug
            });
        }
    }, [selectedDocId, legalDocuments]);

    // Links State
    const { linksData, updateLinksData } = useLinks();
    const [linksForm, setLinksForm] = useState({});

    // Development Engines State
    const { enginesData, updateDevelopmentEnginesData } = useDevelopmentEngines();
    const [enginesForm, setEnginesForm] = useState(defaultDevelopmentEngines);

    // Impact State (Science & Tech)
    const {
        impactData, updateImpactData,
        cases, addCase, updateCase, deleteCase
    } = useImpact();

    const [impactForm, setImpactForm] = useState({
        sectionTitle: '',
        title: '',
        description: '',
        image: ''
    });

    // Cases State
    const [editingCaseId, setEditingCaseId] = useState(null);
    const [caseForm, setCaseForm] = useState({
        title: '',
        date: '',
        description: '',
        image: '',
        imageMetadata: null,
        gallery: [],
        label: 'Estudo de Caso'
    });

    const [selectedLinksCol, setSelectedLinksCol] = useState(0);

    useEffect(() => {
        if (linksData) {
            setLinksForm(JSON.parse(JSON.stringify(linksData)));
        }
    }, [linksData]);

    useEffect(() => {
        if (impactData) {
            setImpactForm(impactData);
        }
    }, [impactData]);

    useEffect(() => {
        if (enginesData) {
            setEnginesForm(enginesData);
        }
    }, [enginesData]);

    // Initialize forms when tab is switched
    useEffect(() => {
        if (activeTab === 'hero') {
            setHeroFormData({
                title: heroData.title,
                summary: heroData.summary,
                tag: heroData.tag
            });
        }
        // Legal tab updates specific handlers via its own useEffect now
        if (activeTab === 'links') {
            setLinksForm(JSON.parse(JSON.stringify(linksData))); // deep copy
        }
        if (activeTab === 'engines') {
            setEnginesForm(enginesData || defaultDevelopmentEngines);
        }
    }, [activeTab, heroData, linksData, enginesData]);

    // News Handlers
    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            const oldIndex = news.findIndex(item => item.id === active.id);
            const newIndex = news.findIndex(item => item.id === over.id);

            // Create a new array with the moved item
            const newOrder = [...news];
            const [movedItem] = newOrder.splice(oldIndex, 1);
            newOrder.splice(newIndex, 0, movedItem);

            // Extract IDs for backend update
            const ids = newOrder.map(item => item.id);
            reorderNews(ids);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Prevent saving a news item before the image upload finishes.
    const [newsImageUploading, setNewsImageUploading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (newsImageUploading) {
            alert('Aguarde o upload da imagem terminar antes de publicar/atualizar.');
            return;
        }
        try {
            if (editingId) {
                await updateNews(editingId, formData);
                showToast('Notícia atualizada com sucesso!');
            } else {
                await addNews(formData);
                showToast('Notícia publicada com sucesso!');
            }
            setEditingId(null);
            setNewsEditorOpen(false);
            setFormData({ title: '', subtitle: '', image: '', imageMetadata: null, content: '', published: true, gallery: [], author: '', publishedAt: '' });
        } catch (error) {
            console.error('Erro ao salvar notícia:', error);
            alert(`Erro ao salvar notícia: ${error.message}`);
        }
    };

    const handleNewNews = () => {
        setEditingId(null);
        setFormData({ title: '', subtitle: '', image: '', imageMetadata: null, content: '', published: true, gallery: [], author: '', publishedAt: '' });
        setNewsEditorOpen(true);
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setFormData({
            title: item.title,
            subtitle: item.subtitle || '',
            image: item.image,
            imageMetadata: item.imageMetadata || null,
            content: item.content || '',
            published: item.published !== undefined ? item.published : true,
            gallery: (item.gallery && Array.isArray(item.gallery)) ? item.gallery : [],
            author: item.author || '',
            publishedAt: item.publishedAt ? item.publishedAt.split('T')[0] : ''
        });
        console.log('[DEBUG] handleEdit - Loaded Gallery:', item.gallery); // Verify what we get from DB
        setNewsEditorOpen(true);
    };

    const handleCancel = () => {
        setEditingId(null);
        setFormData({ title: '', subtitle: '', image: '', imageMetadata: null, content: '', published: true, gallery: [], author: '', publishedAt: '' });
        setNewsEditorOpen(false);
    };




    // Content Handlers
    const handleContentChange = (e) => {
        setContentFormData({ ...contentFormData, [e.target.name]: e.target.value });
    };

    const handleContentSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingContentId) {
                await updateContent(editingContentId, contentFormData);
                setEditingContentId(null);
                showToast('Conteúdo atualizado com sucesso!');
            } else {
                await addContent(contentFormData);
                showToast('Conteúdo publicado com sucesso!');
            }
            setContentFormData({ title: '', description: '', label: '', image: '', imageMetadata: null, author: '', body: '' });
        } catch (error) {
            console.error('Erro ao salvar conteúdo:', error);
            alert(`Erro ao salvar conteúdo: ${error.message}`);
        }
    };

    const handleContentEdit = (item) => {
        setEditingContentId(item.id);
        setContentFormData({
            title: item.title,
            description: item.description,
            label: item.content?.label || item.label || '',
            image: item.content?.image || item.image || '',
            imageMetadata: item.imageMetadata || null,
            author: item.content?.author || item.author || '',
            body: item.body || ''
        });
    };

    const handleContentCancel = () => {
        setEditingContentId(null);
        setContentFormData({ title: '', description: '', label: '', image: '', imageMetadata: null, author: '', body: '' });
    };

    // Hero Handlers
    const handleHeroChange = (e) => {
        setHeroFormData({ ...heroFormData, [e.target.name]: e.target.value });
    };

    const handleHeroUpdate = async (e) => {
        e.preventDefault();
        try {
            await updateHeroData(heroFormData);
            showToast('Seção Destaque atualizada!');
        } catch (error) {
            console.error('Erro ao atualizar hero:', error);
            alert(`Erro ao salvar: ${error.message}`);
        }
    };

    const handleAddMedia = async (e) => {
        e.preventDefault();
        if (!mediaInput) return;
        try {
            await addMedia(mediaInput, mediaType);
            setMediaInput('');
            showToast('Mídia adicionada com sucesso!');
        } catch (error) {
            console.error('Erro ao adicionar mídia:', error);
            alert(`Erro ao adicionar mídia: ${error.message}`);
        }
    };

    // Legal Handlers (Refactored)
    const handleLegalFormChange = (field, value) => {
        setLegalForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveLegal = async () => {
        try {
            // Validation
            if (!legalForm.title) return showToast('O título é obrigatório.', 'error');

            // Slug generation if missing
            let dataToSave = { ...legalForm };
            if (!dataToSave.slug) {
                dataToSave.slug = dataToSave.title
                    .toLowerCase()
                    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-+|-+$/g, '');
            }

            if (selectedDocId === 'new') {
                const newDoc = await createDocument(dataToSave);
                showToast('Documento criado com sucesso!');
                setSelectedDocId(newDoc.id);
            } else {
                await updateDocument(legalForm.id, dataToSave);
                showToast('Documento atualizado com sucesso!');
            }
            refreshDocuments();
        } catch (error) {
            console.error('Erro ao salvar documento:', error);
            showToast(`Erro ao salvar: ${error.message}`, 'error');
        }
    };

    const handleDeleteLegal = async () => {
        if (!window.confirm('Tem certeza que deseja excluir este documento?')) return;
        try {
            await deleteDocument(selectedDocId);
            showToast('Documento excluído.');
            setSelectedDocId(null); // Will trigger default selection
            refreshDocuments();
        } catch (error) {
            console.error('Erro ao excluir:', error);
            showToast('Erro ao excluir documento.', 'error');
        }
    };

    // Safe immutable update for link items
    const handleLinkItemChange = (colIndex, itemIndex, field, value) => {
        console.log(`Updating Link: Col ${colIndex}, Item ${itemIndex}, Field ${field}, Value: "${value}"`);
        setLinksForm(prev => {
            // Deep clone structure to avoid mutation
            const newLinks = { ...prev };
            newLinks.columns = [...prev.columns];
            newLinks.columns[colIndex] = { ...prev.columns[colIndex] };
            newLinks.columns[colIndex].items = [...prev.columns[colIndex].items];

            // Update specific item safely
            newLinks.columns[colIndex].items[itemIndex] = {
                ...newLinks.columns[colIndex].items[itemIndex],
                [field]: value
            };

            return newLinks;
        });
    };
    const handleRemoveLinkItem = (colIndex, itemIndex) => {
        setLinksForm(prev => {
            const newLinks = { ...prev };
            newLinks.columns = [...prev.columns];
            newLinks.columns[colIndex] = { ...newLinks.columns[colIndex] };
            // Splice creates a new array if we spread first? No, splice mutates.
            // Safe way: filter or slice used generally, but here let's copy then splice.
            const newItems = [...prev.columns[colIndex].items];
            newItems.splice(itemIndex, 1);
            newLinks.columns[colIndex].items = newItems;

            return newLinks;
        });
    };

    const [saving, setSaving] = useState(false);
    const [, setSaveSuccess] = useState(false);
    const [internalOverviewDays, setInternalOverviewDays] = useState(7);
    const [internalEventsPage, setInternalEventsPage] = useState(1);
    const [internalEventsPageSize, setInternalEventsPageSize] = useState(25);
    const [internalEventsFilters, setInternalEventsFilters] = useState({
        eventName: '',
        source: '',
        countryCode: '',
        search: '',
        sortBy: 'createdAt',
        sortDir: 'desc'
    });
    const [internalEventsSearchInput, setInternalEventsSearchInput] = useState('');
    const [internalOverviewData, setInternalOverviewData] = useState(null);
    const [internalOverviewLoading, setInternalOverviewLoading] = useState(false);
    const [internalOverviewError, setInternalOverviewError] = useState('');
    const [cookieDetailsDialogOpen, setCookieDetailsDialogOpen] = useState(false);
    const [selectedCookieEvent, setSelectedCookieEvent] = useState(null);
    const [seoHealthData, setSeoHealthData] = useState(null);
    const [seoHealthLoading, setSeoHealthLoading] = useState(false);
    const [seoHealthError, setSeoHealthError] = useState('');
    const [contactRoutingForm, setContactRoutingForm] = useState({
        from: '',
        toList: [],
        bccList: [],
        whatsappToList: [],
        source: ''
    });
    const [contactRoutingDraft, setContactRoutingDraft] = useState({
        to: '',
        bcc: '',
        whatsappTo: ''
    });
    const [contactRoutingLoading, setContactRoutingLoading] = useState(false);
    const [contactRoutingSaving, setContactRoutingSaving] = useState(false);
    const [contactRoutingError, setContactRoutingError] = useState('');



    const handleLinksUpdate = async (e) => {
        e.preventDefault();
        setSaving(true);
        setSaveSuccess(false);
        console.log("Saving Links Data:", JSON.stringify(linksForm, null, 2));
        try {
            await updateLinksData(linksForm);
            setSaveSuccess(true);
            showToast('Seção O Que Fazemos atualizada com sucesso!');
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (error) {
            console.error('Erro ao atualizar Seção O Que Fazemos:', error);
            // Log full error object to console for debugging
            console.log('Full Error Details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
            alert(`Erro ao salvar Seção O Que Fazemos: ${error.message || 'Erro desconhecido'}`);
        } finally {
            setSaving(false);
        }
    };

    // Unified Reorder Logic (Manual or Drag)
    const handleReorderLinks = async (colIndex, newItems) => {
        setLinksForm(prev => {
            const newLinks = { ...prev };
            newLinks.columns = [...prev.columns];
            newLinks.columns[colIndex] = {
                ...prev.columns[colIndex],
                items: newItems
            };

            // Auto-persist to backend
            setTimeout(async () => {
                try {
                    await updateLinksData(newLinks);
                    showToast('Ordem atualizada!');
                } catch (error) {
                    showToast('Erro ao salvar ordem', 'error');
                }
            }, 0);

            return newLinks;
        });
    };

    // Directional Reordering Handler (Up/Down Buttons)
    const handleMoveLinksDirectional = (colIndex, oldIndex, direction) => {
        const items = [...linksForm.columns[colIndex].items];
        const newIndex = oldIndex + direction;

        if (newIndex < 0 || newIndex >= items.length) return;

        const newItems = arrayMove(items, oldIndex, newIndex);
        handleReorderLinks(colIndex, newItems);
    };

    // Links Drag Handler
    const handleDragEndLinks = (event, colIndex) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const items = [...linksForm.columns[colIndex].items];
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            const newItems = arrayMove(items, oldIndex, newIndex);
            handleReorderLinks(colIndex, newItems);
        }
    };

    const handleEnginesSectionTitleChange = (value) => {
        setEnginesForm((prev) => ({ ...prev, sectionTitle: value }));
    };

    const handleEngineFieldChange = (engineIndex, field, value) => {
        setEnginesForm((prev) => {
            const updated = { ...prev, engines: [...(prev.engines || [])] };
            const currentEngine = { ...(updated.engines[engineIndex] || {}) };
            currentEngine[field] = value;
            updated.engines[engineIndex] = currentEngine;
            return updated;
        });
    };

    const handleEngineCapabilityChange = (engineIndex, capabilityIndex, value) => {
        setEnginesForm((prev) => {
            const updated = { ...prev, engines: [...(prev.engines || [])] };
            const currentEngine = { ...(updated.engines[engineIndex] || {}) };
            const currentCaps = Array.isArray(currentEngine.capabilities) ? [...currentEngine.capabilities] : ['', '', ''];
            currentCaps[capabilityIndex] = value;
            currentEngine.capabilities = currentCaps;
            updated.engines[engineIndex] = currentEngine;
            return updated;
        });
    };

    const handleSaveDevelopmentEngines = async () => {
        try {
            await updateDevelopmentEnginesData(enginesForm);
            showToast('Motores de Desenvolvimento atualizados com sucesso!');
        } catch (error) {
            console.error('Erro ao atualizar motores:', error);
            showToast(`Erro ao salvar motores: ${error.message}`, 'error');
        }
    };

    // Cases Handlers
    const handleCaseSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingCaseId) {
                await updateCase(editingCaseId, caseForm);
                setEditingCaseId(null);
                showToast('Caso de sucesso atualizado!');
            } else {
                await addCase(caseForm);
                showToast('Caso de sucesso adicionado!');
            }
            setCaseForm({ title: '', date: '', description: '', image: '', imageMetadata: null, gallery: [], label: 'Estudo de Caso' });
        } catch (error) {
            console.error('Erro ao salvar caso:', error);
            alert(`Erro ao salvar caso: ${error.message}`);
        }
    };

    const handleCaseEdit = (item) => {
        setEditingCaseId(item.id);
        setCaseForm({
            title: item.title,
            date: item.date,
            description: item.description,
            image: item.image,
            imageMetadata: item.imageMetadata || null,
            gallery: (item.gallery && Array.isArray(item.gallery)) ? item.gallery : [],
            label: item.label
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCaseCancel = () => {
        setEditingCaseId(null);
        setCaseForm({ title: '', date: '', description: '', image: '', imageMetadata: null, gallery: [], label: 'Estudo de Caso' });
    };

    const loadInternalOverview = useCallback(async (daysArg, pageArg = 1, pageSizeArg = 25, filtersArg = {}) => {
        const days = Number.isFinite(Number(daysArg)) ? Number(daysArg) : 7;
        const page = Number.isFinite(Number(pageArg)) ? Math.max(1, Number(pageArg)) : 1;
        const pageSize = Number.isFinite(Number(pageSizeArg)) ? Math.max(1, Number(pageSizeArg)) : 25;
        const filters = {
            eventName: String(filtersArg?.eventName || '').trim(),
            source: String(filtersArg?.source || '').trim(),
            countryCode: String(filtersArg?.countryCode || '').trim(),
            search: String(filtersArg?.search || '').trim(),
            sortBy: String(filtersArg?.sortBy || 'createdAt').trim(),
            sortDir: String(filtersArg?.sortDir || 'desc').trim()
        };
        try {
            setInternalOverviewLoading(true);
            setInternalOverviewError('');
            const data = await getInternalAnalyticsOverview(days, {
                page,
                pageSize,
                ...filters
            });
            setInternalOverviewData(data);
            const resolvedDays = Number(data?.period?.days || days);
            const resolvedPage = Number(data?.recentEventsPagination?.page || page);
            const resolvedPageSize = Number(data?.recentEventsPagination?.pageSize || pageSize);
            setInternalOverviewDays(resolvedDays);
            setInternalEventsPage(resolvedPage);
            setInternalEventsPageSize(resolvedPageSize);
        } catch (error) {
            const message = error?.message || 'Falha ao carregar dados de analytics.';
            setInternalOverviewError(message);
        } finally {
            setInternalOverviewLoading(false);
        }
    }, []);

    const handleInternalOverviewDaysChange = (days) => {
        setInternalOverviewDays(days);
        setInternalEventsPage(1);
    };

    const handleInternalEventsPageChange = (nextPage) => {
        setInternalEventsPage(Math.max(1, Number(nextPage) || 1));
    };

    const handleInternalEventsPageSizeChange = (event) => {
        const nextPageSize = Number.parseInt(event.target.value, 10);
        const safePageSize = Number.isFinite(nextPageSize) && nextPageSize > 0 ? nextPageSize : 25;
        setInternalEventsPageSize(safePageSize);
        setInternalEventsPage(1);
    };

    const handleInternalEventsFilterChange = (field, value) => {
        setInternalEventsFilters((prev) => ({ ...prev, [field]: value }));
        setInternalEventsPage(1);
    };

    const handleApplyInternalEventsSearch = () => {
        const normalizedSearch = String(internalEventsSearchInput || '').trim();
        setInternalEventsFilters((prev) => ({ ...prev, search: normalizedSearch }));
        setInternalEventsPage(1);
    };

    const handleClearInternalEventsFilters = () => {
        setInternalEventsFilters({
            eventName: '',
            source: '',
            countryCode: '',
            search: '',
            sortBy: 'createdAt',
            sortDir: 'desc'
        });
        setInternalEventsSearchInput('');
        setInternalEventsPage(1);
    };

    const loadSeoHealthOverview = useCallback(async () => {
        try {
            setSeoHealthLoading(true);
            setSeoHealthError('');
            const data = await getSeoHealthOverview();
            setSeoHealthData(data);
        } catch (error) {
            const message = error?.message || 'Falha ao carregar monitor de SEO.';
            setSeoHealthError(message);
        } finally {
            setSeoHealthLoading(false);
        }
    }, []);

    const loadContactRoutingConfig = useCallback(async () => {
        try {
            setContactRoutingLoading(true);
            setContactRoutingError('');
            const data = await getContactRoutingConfig();
            const toList = Array.isArray(data?.to)
                ? data.to
                : String(data?.toText || '')
                    .split(/[,\n;]+/)
                    .map((item) => item.trim().toLowerCase())
                    .filter(Boolean);
            const bccList = Array.isArray(data?.bcc)
                ? data.bcc
                : String(data?.bccText || '')
                    .split(/[,\n;]+/)
                    .map((item) => item.trim().toLowerCase())
                    .filter(Boolean);
            const whatsappToList = Array.isArray(data?.whatsappTo)
                ? data.whatsappTo
                : String(data?.whatsappToText || '')
                    .split(/[,\n;]+/)
                    .map((item) => item.trim())
                    .filter(Boolean);
            setContactRoutingForm({
                from: data?.from || '',
                toList: Array.from(new Set(toList)),
                bccList: Array.from(new Set(bccList)),
                whatsappToList: Array.from(new Set(whatsappToList)),
                source: data?.source || ''
            });
            setContactRoutingDraft({ to: '', bcc: '', whatsappTo: '' });
        } catch (error) {
            setContactRoutingError(error?.message || 'Não foi possível carregar os destinatários de contato.');
        } finally {
            setContactRoutingLoading(false);
        }
    }, []);

    const handleContactRoutingChange = (field, value) => {
        setContactRoutingForm((prev) => ({ ...prev, [field]: value }));
    };

    const splitRecipients = (input) => (
        String(input || '')
            .split(/[,\n;]+/)
            .map((item) => item.trim().toLowerCase())
            .filter(Boolean)
    );

    const handleRecipientDraftChange = (type, value) => {
        setContactRoutingDraft((prev) => ({ ...prev, [type]: value }));
    };

    const handleAddRecipients = (type) => {
        const listKey = type === 'bcc' ? 'bccList' : type === 'whatsappTo' ? 'whatsappToList' : 'toList';
        const draftValue = contactRoutingDraft[type];
        const parsed = type === 'whatsappTo'
            ? String(draftValue || '')
                .split(/[,\n;]+/)
                .map((item) => item.replace(/\D+/g, '').trim())
                .filter(Boolean)
            : splitRecipients(draftValue);
        if (parsed.length === 0) return;

        setContactRoutingForm((prev) => ({
            ...prev,
            [listKey]: Array.from(new Set([...(prev[listKey] || []), ...parsed]))
        }));
        setContactRoutingDraft((prev) => ({ ...prev, [type]: '' }));
    };

    const handleRemoveRecipient = (type, email) => {
        const listKey = type === 'bcc' ? 'bccList' : type === 'whatsappTo' ? 'whatsappToList' : 'toList';
        setContactRoutingForm((prev) => ({
            ...prev,
            [listKey]: (prev[listKey] || []).filter((item) => item !== email)
        }));
    };

    const handleSaveContactRouting = async () => {
        try {
            setContactRoutingSaving(true);
            setContactRoutingError('');
            if (
                (!Array.isArray(contactRoutingForm.toList) || contactRoutingForm.toList.length === 0) &&
                (!Array.isArray(contactRoutingForm.whatsappToList) || contactRoutingForm.whatsappToList.length === 0)
            ) {
                throw new Error('Adicione ao menos um destinatário por e-mail ou WhatsApp.');
            }
            const updated = await updateContactRoutingConfig({
                from: contactRoutingForm.from,
                to: contactRoutingForm.toList,
                bcc: contactRoutingForm.bccList,
                whatsappTo: contactRoutingForm.whatsappToList
            });
            const updatedToList = Array.isArray(updated?.to)
                ? updated.to
                : splitRecipients(updated?.toText || '');
            const updatedBccList = Array.isArray(updated?.bcc)
                ? updated.bcc
                : splitRecipients(updated?.bccText || '');
            const updatedWhatsappToList = Array.isArray(updated?.whatsappTo)
                ? updated.whatsappTo
                : String(updated?.whatsappToText || '')
                    .split(/[,\n;]+/)
                    .map((item) => item.trim())
                    .filter(Boolean);
            setContactRoutingForm((prev) => ({
                ...prev,
                from: updated?.from || '',
                toList: Array.from(new Set(updatedToList)),
                bccList: Array.from(new Set(updatedBccList)),
                whatsappToList: Array.from(new Set(updatedWhatsappToList)),
                source: updated?.source || 'database'
            }));
            setContactRoutingDraft({ to: '', bcc: '', whatsappTo: '' });
            showToast('Destinatários do formulário de contato atualizados!');
        } catch (error) {
            const message = error?.message || 'Falha ao salvar destinatários do contato.';
            setContactRoutingError(message);
            showToast(message, 'error');
        } finally {
            setContactRoutingSaving(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'internalBase' || activeTab === 'executivePanel') {
            loadInternalOverview(internalOverviewDays, internalEventsPage, internalEventsPageSize, internalEventsFilters);
        }
    }, [activeTab, loadInternalOverview, internalOverviewDays, internalEventsPage, internalEventsPageSize, internalEventsFilters]);

    useEffect(() => {
        if (activeTab === 'seoHealth' || activeTab === 'executivePanel') {
            loadSeoHealthOverview();
        }
    }, [activeTab, loadSeoHealthOverview]);

    useEffect(() => {
        if (activeTab === 'contactRouting') {
            loadContactRoutingConfig();
        }
    }, [activeTab, loadContactRoutingConfig]);

    const formatInt = (value) => new Intl.NumberFormat('pt-BR').format(Number(value || 0));
    const formatDateTime = (value) => {
        if (!value) return '-';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '-';
        return date.toLocaleString('pt-BR');
    };
    const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;
    const normalizeTopItems = (items = []) => {
        const maxValue = Math.max(1, ...items.map((item) => Number(item?.value || 0)));
        return items.map((item, index) => ({
            ...item,
            rank: index + 1,
            numericValue: Number(item?.value || 0),
            width: `${Math.max(6, Math.round((Number(item?.value || 0) / maxValue) * 100))}%`
        }));
    };
    const formatTopPageLabel = (label) => {
        const raw = String(label || '').trim();
        if (!raw || raw === '(sem path)') return '(sem path)';
        if (raw === '/') return 'Home (/)';
        if (raw === '/m') return 'Home Mobile (/m)';

        if (raw.startsWith('http://') || raw.startsWith('https://')) {
            try {
                const url = new URL(raw);
                if (url.pathname === '/') return `Home (${url.hostname})`;
                return url.pathname || '(sem path)';
            } catch (_) {
                return raw;
            }
        }

        return raw;
    };
    const getCookieSnapshotSummary = (cookieSnapshot) => {
        if (!cookieSnapshot || typeof cookieSnapshot !== 'object') {
            return { totalCount: 0, usefulCount: 0, namesPreview: [] };
        }
        const totalCount = Number(cookieSnapshot.totalCount || 0);
        const usefulCount = Number(cookieSnapshot.usefulCount || 0);
        const namesPreview = Array.isArray(cookieSnapshot.names)
            ? cookieSnapshot.names.filter(Boolean).slice(0, 4)
            : [];
        return { totalCount, usefulCount, namesPreview };
    };
    const handleOpenCookieDetails = (event) => {
        setSelectedCookieEvent(event || null);
        setCookieDetailsDialogOpen(true);
    };
    const handleCloseCookieDetails = () => {
        setCookieDetailsDialogOpen(false);
        setSelectedCookieEvent(null);
    };

    const kpis = internalOverviewData?.kpis || {};
    const funnel = internalOverviewData?.funnel || {};
    const leadConversionRate = kpis.leadFormViews > 0 ? (kpis.leadFormSubmits / kpis.leadFormViews) * 100 : 0;
    const whatsappShare = kpis.leadFormSubmits > 0 ? (kpis.whatsappClicks / kpis.leadFormSubmits) * 100 : 0;
    const eventsByDay = internalOverviewData?.eventsByDay || [];
    const maxDayValue = Math.max(1, ...eventsByDay.map((item) => Number(item?.value || 0)));
    const dayLabelStep = eventsByDay.length > 12 ? 3 : eventsByDay.length > 7 ? 2 : 1;
    const topSources = normalizeTopItems(internalOverviewData?.topSources || []);
    const sourceGroups = normalizeTopItems(internalOverviewData?.sourceGroups || []);
    const topCountries = normalizeTopItems(internalOverviewData?.topCountries || []);
    const topStates = normalizeTopItems(internalOverviewData?.topStates || internalOverviewData?.topCountries || []);
    const topCities = normalizeTopItems(internalOverviewData?.topCities || []);
    const topPages = normalizeTopItems(
        (internalOverviewData?.topPages || []).map((item) => ({
            ...item,
            label: formatTopPageLabel(item?.label)
        }))
    );
    const topValidationFields = normalizeTopItems(internalOverviewData?.funnel?.topValidationFields || []);
    const topSubmitErrorReasons = normalizeTopItems(internalOverviewData?.funnel?.topSubmitErrorReasons || []);
    const funnelTopPagesRaw = Array.isArray(internalOverviewData?.funnel?.topLeadPages)
        ? internalOverviewData.funnel.topLeadPages
        : [];
    const maxFunnelPageViews = Math.max(1, ...funnelTopPagesRaw.map((item) => Number(item?.views || 0)));
    const funnelTopPages = funnelTopPagesRaw.map((item, index) => ({
        ...item,
        rank: index + 1,
        label: formatTopPageLabel(item?.label),
        width: `${Math.max(6, Math.round((Number(item?.views || 0) / maxFunnelPageViews) * 100))}%`
    }));
    const regionalFocus = internalOverviewData?.regionalFocus || {};
    const totalSourceGroupEvents = sourceGroups.reduce((sum, item) => sum + Number(item?.numericValue || 0), 0);
    const organicShare = totalSourceGroupEvents > 0
        ? ((sourceGroups.find((item) => item.label === 'Busca orgânica')?.numericValue || 0) / totalSourceGroupEvents) * 100
        : 0;
    const directShare = totalSourceGroupEvents > 0
        ? ((sourceGroups.find((item) => item.label === 'Direto')?.numericValue || 0) / totalSourceGroupEvents) * 100
        : 0;
    const socialShare = totalSourceGroupEvents > 0
        ? ((sourceGroups.find((item) => item.label === 'Social / Comunidades')?.numericValue || 0) / totalSourceGroupEvents) * 100
        : 0;
    const funnelSteps = [
        {
            key: 'views',
            label: 'Viram o formulário',
            value: Number(funnel.views || 0),
            helper: 'lead_form_view',
            accent: 'linear-gradient(135deg,#2563eb,#38bdf8)'
        },
        {
            key: 'starts',
            label: 'Começaram a preencher',
            value: Number(funnel.starts || 0),
            helper: 'lead_form_start',
            accent: 'linear-gradient(135deg,#7c3aed,#a855f7)'
        },
        {
            key: 'submitIntents',
            label: 'Tentaram enviar',
            value: Number(funnel.submitIntents || 0),
            helper: 'submit_intent / fallback',
            accent: 'linear-gradient(135deg,#f97316,#fb7185)'
        },
        {
            key: 'validationErrors',
            label: 'Barrados por validação',
            value: Number(funnel.validationErrors || 0),
            helper: 'lead_form_validation_error',
            accent: 'linear-gradient(135deg,#ef4444,#f97316)'
        },
        {
            key: 'submitSuccess',
            label: 'Envios concluídos',
            value: Number(funnel.submitSuccess || 0),
            helper: 'lead_form_submit_success',
            accent: 'linear-gradient(135deg,#16a34a,#22c55e)'
        }
    ];
    const funnelInsights = [];
    if (Number(funnel.views || 0) > 0 && Number(funnel.starts || 0) === 0) {
        funnelInsights.push('A nova leitura de início de preenchimento começa a ser coletada a partir desta atualização.');
    } else if (Number(funnel.dropOffBeforeStart || 0) > 0) {
        funnelInsights.push(`${formatInt(funnel.dropOffBeforeStart)} visitantes viram o formulário, mas não começaram a preencher.`);
    }
    if (Number(funnel.dropOffBeforeIntent || 0) > 0) {
        funnelInsights.push(`${formatInt(funnel.dropOffBeforeIntent)} usuários começaram o preenchimento, mas desistiram antes da tentativa de envio.`);
    }
    if (Number(funnel.validationErrors || 0) > 0) {
        funnelInsights.push(`${formatPercent(funnel.validationShareRate)} das tentativas travaram na validação antes do envio.`);
    }
    if (Number(funnel.submitErrors || 0) > 0) {
        funnelInsights.push(`${formatPercent(funnel.submitErrorRate)} das tentativas válidas falharam no envio técnico.`);
    } else if (Number(funnel.submitAttempts || 0) > 0) {
        funnelInsights.push('Os envios válidos não mostraram erro técnico no período analisado.');
    }
    if (funnelInsights.length === 0) {
        funnelInsights.push('Ainda sem amostra suficiente para diagnosticar o gargalo do funil nesta janela.');
    }
    const executiveHeadlineCards = [
        { label: 'Page Views', value: kpis.pageViews, note: 'visibilidade real do portal', accent: 'linear-gradient(135deg,#2563eb,#38bdf8)' },
        { label: 'Sessões únicas', value: kpis.uniqueSessions, note: 'janela executiva filtrada', accent: 'linear-gradient(135deg,#7c3aed,#a855f7)' },
        { label: 'Leads enviados', value: kpis.leadFormSubmits, note: 'envios concluídos', accent: 'linear-gradient(135deg,#16a34a,#22c55e)' },
        { label: 'Cliques WhatsApp', value: kpis.whatsappClicks, note: 'intenção comercial direta', accent: 'linear-gradient(135deg,#0ea5e9,#22c55e)' }
    ];
    const eventFilterOptions = Array.from(new Set((internalOverviewData?.topEvents || []).map((item) => item?.label).filter(Boolean)));
    const sourceFilterOptions = Array.from(new Set(topSources.map((item) => item?.label).filter(Boolean)));
    const countryFilterOptions = Array.from(new Set(topCountries.map((item) => item?.label).filter(Boolean)));
    const activeTableFiltersCount = [
        internalEventsFilters.eventName,
        internalEventsFilters.source,
        internalEventsFilters.countryCode,
        internalEventsFilters.search
    ].filter((value) => String(value || '').trim() !== '').length;
    const recentEventsPagination = internalOverviewData?.recentEventsPagination || {
        page: internalEventsPage,
        pageSize: internalEventsPageSize,
        total: (internalOverviewData?.recentEvents || []).length,
        totalPages: 1
    };
    const seoChecks = Array.isArray(seoHealthData?.checks) ? seoHealthData.checks : [];
    const seoFailedChecks = seoChecks.filter((check) => !check?.ok);
    const seoSummary = seoHealthData?.summary || {};
    const seoTechnical = seoHealthData?.technical || {};
    const legacyUrlMonitorItems = Array.isArray(seoHealthData?.legacyUrlsMonitor?.items) ? seoHealthData.legacyUrlsMonitor.items : [];
    const priorityPageMonitorItems = Array.isArray(seoHealthData?.priorityPagesMonitor?.items) ? seoHealthData.priorityPagesMonitor.items : [];
    const seoExecutiveSummary = seoHealthData?.executiveSummary || {};
    const seoTechnicalPassedCount = Number(seoSummary?.passedChecks || 0);
    const seoTechnicalTotalCount = Math.max(1, Number(seoSummary?.totalChecks || 0));
    const seoPriorityHealthyCount = Number(seoHealthData?.priorityPagesMonitor?.healthy || 0);
    const seoPriorityTotalCount = Math.max(1, Number(seoHealthData?.priorityPagesMonitor?.total || 0));
    const seoLegacyContainedCount = Number(seoHealthData?.legacyUrlsMonitor?.contained || 0);
    const seoLegacyTotalCount = Math.max(1, Number(seoHealthData?.legacyUrlsMonitor?.total || 0));
    const seoSemaphores = [
        {
            key: 'technical',
            label: 'Base técnica',
            value: `${formatInt(seoTechnicalPassedCount)}/${formatInt(seoTechnicalTotalCount)}`,
            ratio: seoTechnicalPassedCount / seoTechnicalTotalCount,
            tone: seoFailedChecks.length === 0 ? 'good' : 'warn',
            headline: seoFailedChecks.length === 0 ? 'Sem falhas abertas' : `${formatInt(seoFailedChecks.length)} pontos em atenção`,
            detail: 'robots, sitemap, canonical e resposta do domínio'
        },
        {
            key: 'priority',
            label: 'Páginas estratégicas',
            value: `${formatInt(seoPriorityHealthyCount)}/${formatInt(seoPriorityTotalCount)}`,
            ratio: seoPriorityHealthyCount / seoPriorityTotalCount,
            tone: seoPriorityHealthyCount === seoPriorityTotalCount ? 'good' : 'warn',
            headline: seoPriorityHealthyCount === seoPriorityTotalCount ? 'Todas entregando sinais corretos' : 'Parte das páginas ainda precisa de ajuste',
            detail: 'title, robots e canonical no HTML inicial do crawler'
        },
        {
            key: 'legacy',
            label: 'URLs legadas',
            value: `${formatInt(seoLegacyContainedCount)}/${formatInt(seoLegacyTotalCount)}`,
            ratio: seoLegacyContainedCount / seoLegacyTotalCount,
            tone: seoLegacyContainedCount === seoLegacyTotalCount ? 'good' : 'warn',
            headline: seoLegacyContainedCount === seoLegacyTotalCount ? 'Rotas antigas contidas' : 'Ainda há rotas antigas expostas',
            detail: '404 real no edge e bloqueio de indexação'
        }
    ];
    const seoPulseSegments = seoSemaphores.map((item) => ({
        ...item,
        percent: Math.round(item.ratio * 100)
    }));
    const seoStatus = String(seoSummary?.status || 'unknown');
    const seoStatusLabel = seoStatus === 'healthy'
        ? 'Saudável'
        : seoStatus === 'attention'
            ? 'Atenção'
            : seoStatus === 'critical'
                ? 'Crítico'
                : 'N/D';
    const seoStatusColor = seoStatus === 'healthy'
        ? '#16a34a'
        : seoStatus === 'attention'
            ? '#f59e0b'
            : '#dc2626';
    const executivePriorityItems = [
        {
            title: 'Recuperar conversão do contato',
            detail: leadConversionRate > 0
                ? `${formatPercent(leadConversionRate)} de conversão do formulário na janela atual.`
                : 'Ainda sem conversão concluída do formulário nesta janela.'
        },
        {
            title: 'Crescer aquisição orgânica',
            detail: `Busca orgânica representa ${formatPercent(organicShare)} do mix limpo de aquisição.`
        },
        {
            title: 'Expandir fora do DF',
            detail: `${formatPercent(regionalFocus?.nonDfShare || 0)} dos eventos vieram de fora do DF; São Paulo responde por ${formatPercent(regionalFocus?.saoPauloShare || 0)}.`
        },
        {
            title: 'Manter SEO técnico íntegro',
            detail: `Score atual de SEO técnico em ${Number(seoSummary?.healthScore || 0)} com ${Number(seoFailedChecks.length || 0)} falhas abertas.`
        }
    ];
    const internalKpiCards = [
        { label: 'Eventos', value: internalOverviewData?.kpis?.totalEvents, accent: 'linear-gradient(90deg,#ef4444,#f97316)' },
        { label: 'Sessões únicas', value: internalOverviewData?.kpis?.uniqueSessions, accent: 'linear-gradient(90deg,#0ea5e9,#22d3ee)' },
        { label: 'Clientes únicos', value: internalOverviewData?.kpis?.uniqueClients, accent: 'linear-gradient(90deg,#6366f1,#a855f7)' },
        { label: 'Page views', value: internalOverviewData?.kpis?.pageViews, accent: 'linear-gradient(90deg,#14b8a6,#10b981)' },
        { label: 'Lead form views', value: internalOverviewData?.kpis?.leadFormViews, accent: 'linear-gradient(90deg,#f97316,#fb7185)' },
        { label: 'Leads enviados', value: internalOverviewData?.kpis?.leadFormSubmits, accent: 'linear-gradient(90deg,#22c55e,#0ea5e9)' },
        { label: 'Cliques WhatsApp', value: internalOverviewData?.kpis?.whatsappClicks, accent: 'linear-gradient(90deg,#22c55e,#16a34a)' },
        { label: 'Contatos (formulário)', value: internalOverviewData?.kpis?.contactMessages, accent: 'linear-gradient(90deg,#334155,#64748b)' }
    ];


    // Dynamic documents now loaded from legalFormData.documents

    const neutralGray = '#475467';
    const offBlack = '#1a1a1a';
    const brandRed = '#CC0000';
    const routingCardSx = {
        p: { xs: 2, md: 2.15 },
        border: '1px solid #e7ecf2',
        borderRadius: '16px',
        backgroundColor: '#fcfdff',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
    };
    const routingAddButtonSx = {
        borderRadius: '12px',
        minWidth: 112,
        height: 40,
        px: 2,
        whiteSpace: 'nowrap',
        borderColor: '#d7dee8',
        color: offBlack,
        backgroundColor: '#fff'
    };
    const routingChipWrapSx = {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.8,
        minHeight: 68,
        alignContent: 'flex-start',
        padding: '10px',
        borderRadius: '12px',
        backgroundColor: '#fff',
        border: '1px solid #e9edf3'
    };
    const routingChannelCards = [
        {
            key: 'to',
            title: 'Destinatários TO',
            description: 'Recebem todos os contatos principais do formulário.',
            label: 'Adicionar e-mail TO',
            placeholder: 'contato@r5id.com.br',
            icon: <MailOutlineIcon sx={{ fontSize: 18, color: brandRed }} />,
            buttonHover: { borderColor: brandRed, backgroundColor: '#fff5f5' },
            items: contactRoutingForm.toList || [],
            itemWeight: 500
        },
        {
            key: 'bcc',
            title: 'Destinatários BCC',
            description: 'Cópia oculta para auditoria, operação ou acompanhamento interno.',
            label: 'Adicionar e-mail BCC',
            placeholder: 'gestao@r5id.com.br',
            icon: <MailOutlineIcon sx={{ fontSize: 18, color: '#8c96a3' }} />,
            buttonHover: { borderColor: brandRed, backgroundColor: '#fff5f5' },
            items: contactRoutingForm.bccList || [],
            itemWeight: 500
        },
        {
            key: 'whatsappTo',
            title: 'Destinatários WhatsApp',
            description: 'Esses números recebem os alertas internos do formulário no WhatsApp, sem depender do e-mail.',
            label: 'Adicionar WhatsApp',
            placeholder: '5561981359883',
            icon: <WhatsAppIcon sx={{ fontSize: 19, color: '#128C7E' }} />,
            buttonHover: { borderColor: '#128C7E', backgroundColor: '#f2fbf8' },
            items: contactRoutingForm.whatsappToList || [],
            itemWeight: 600
        }
    ];

    return (
        <Container maxWidth="lg" sx={{ py: 5 }}>
            <Toast
                message={toast?.message}
                type={toast?.type}
                onClose={handleCloseToast}
            />

            <Typography
                variant="h4"
                component="h1"
                sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: offBlack,
                    letterSpacing: '-0.02em'
                }}
            >
                Admin Experience
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6 }}>
                <Typography variant="body1" sx={{ color: neutralGray, fontWeight: 400 }}>
                    Gerencie todos os aspectos do seu portal com ferramentas intuitivas.
                </Typography>
                <Box sx={{
                    backgroundColor: 'rgba(204, 0, 0, 0.05)',
                    color: brandRed,
                    border: '1px solid rgba(204, 0, 0, 0.1)',
                    borderRadius: '14px',
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 700
                }}>
                    v{packageJson.version}
                </Box>
            </Box>

            <Box
                sx={{
                    display: { xs: 'block', lg: 'grid' },
                    gridTemplateColumns: { lg: sidebarExpanded ? '280px minmax(0, 1fr)' : '92px minmax(0, 1fr)' },
                    gap: 3,
                    alignItems: 'start'
                }}
            >
                {isLargeDesktop ? (
                    <Paper
                        elevation={0}
                        sx={{
                            position: 'sticky',
                            top: 16,
                            borderRadius: '20px',
                            border: '1px solid rgba(0,0,0,0.08)',
                            overflow: 'hidden'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: sidebarExpanded ? 'space-between' : 'center',
                                px: sidebarExpanded ? 2 : 1,
                                py: 1.5,
                                borderBottom: '1px solid rgba(0,0,0,0.08)',
                                backgroundColor: '#fafafa'
                            }}
                        >
                            {sidebarExpanded && (
                                <Typography variant="caption" sx={{ color: neutralGray, fontWeight: 700, letterSpacing: '0.08em' }}>
                                    MENU ADMIN
                                </Typography>
                            )}
                            <IconButton
                                size="small"
                                onClick={() => setSidebarExpanded((prev) => !prev)}
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '10px',
                                    color: neutralGray
                                }}
                                aria-label={sidebarExpanded ? 'Recolher menu lateral' : 'Expandir menu lateral'}
                            >
                                {sidebarExpanded ? <MenuOpenIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
                            </IconButton>
                        </Box>

                        <List sx={{ p: 1 }}>
                            {adminNavItems.map((item) => {
                                const selected = activeTab === item.value;
                                return (
                                    <Tooltip key={item.value} title={sidebarExpanded ? '' : item.label} placement="right">
                                        <ListItemButton
                                            selected={selected}
                                            onClick={() => setActiveTab(item.value)}
                                            sx={{
                                                borderRadius: '12px',
                                                minHeight: 44,
                                                px: sidebarExpanded ? 1.25 : 1,
                                                justifyContent: sidebarExpanded ? 'flex-start' : 'center',
                                                '&.Mui-selected': {
                                                    backgroundColor: 'rgba(204, 0, 0, 0.08)',
                                                    color: brandRed
                                                },
                                                '&.Mui-selected:hover': {
                                                    backgroundColor: 'rgba(204, 0, 0, 0.14)'
                                                }
                                            }}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    minWidth: sidebarExpanded ? 34 : 0,
                                                    color: selected ? brandRed : neutralGray,
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {item.icon}
                                            </ListItemIcon>
                                            {sidebarExpanded && (
                                                <ListItemText
                                                    primary={item.label}
                                                    primaryTypographyProps={{
                                                        fontWeight: selected ? 700 : 600,
                                                        fontSize: '0.88rem'
                                                    }}
                                                />
                                            )}
                                        </ListItemButton>
                                    </Tooltip>
                                );
                            })}
                        </List>
                    </Paper>
                ) : (
                    <Box
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1000,
                            backgroundColor: 'rgba(255, 255, 255, 0.98)',
                            backdropFilter: 'blur(10px)',
                            borderBottom: '1px solid rgba(0,0,0,0.05)',
                            mb: 5,
                            mx: -2,
                            px: 2,
                            borderRadius: '0 0 16px 16px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                        }}
                    >
                        <Tabs
                            value={activeTab}
                            onChange={(e, newValue) => setActiveTab(newValue)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                '& .MuiTab-root': {
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    letterSpacing: '0.05em',
                                    minWidth: 100,
                                    py: 3,
                                    color: '#888',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        color: offBlack,
                                        backgroundColor: 'rgba(0,0,0,0.02)'
                                    }
                                },
                                '& .Mui-selected': {
                                    color: `${brandRed} !important`,
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: brandRed,
                                    height: 3,
                                    borderRadius: '50px 50px 0 0'
                                }
                            }}
                        >
                            {adminNavItems.map((item) => (
                                <Tab key={item.value} icon={item.icon} iconPosition="start" label={item.label} value={item.value} />
                            ))}
                        </Tabs>
                    </Box>
                )}

                <Box sx={{ minWidth: 0 }}>

            {activeTab === 'trainingManagement' && <TrainingManagement />}
            {activeTab === 'news' && (
                /* NEWS SECTION */
                <Box>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            mb: 3,
                            borderRadius: '16px',
                            border: '1px solid rgba(0,0,0,0.08)',
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2
                        }}
                    >
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: offBlack, lineHeight: 1.1 }}>
                                Notícias
                            </Typography>
                            <Typography variant="body2" sx={{ color: neutralGray, mt: 0.75 }}>
                                Clique em Editar para abrir um painel grande de edição (conteúdo com editor rico).
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            onClick={handleNewNews}
                            disabled={newsImageUploading}
                            sx={{
                                backgroundColor: brandRed,
                                '&:hover': { backgroundColor: '#CC0000' },
                                borderRadius: '999px',
                                px: 2.25
                            }}
                        >
                            Nova Notícia
                        </Button>
                    </Paper>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Notícias Atuais</Typography>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            Arraste os itens para reordenar. A página principal exibe as 3 primeiras notícias na ordem abaixo.
                        </Alert>

                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={news.map(item => item.id)}
                                strategy={rectSortingStrategy}
                            >
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', // Token 320
                                    gap: '24px' // Token 24
                                }}>
                                    {news.map(item => (
                                        <Box key={item.id}>
                                            <SortableItem id={item.id}>
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        borderRadius: '24px',
                                                        overflow: 'hidden',
                                                        border: '1px solid rgba(0,0,0,0.05)',
                                                        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                                        backgroundColor: '#fff',
                                                        '&:hover': {
                                                            transform: 'translateY(-8px)',
                                                            boxShadow: '0 20px 40px rgba(0,0,0,0.06)',
                                                            borderColor: brandRed
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ height: 180, overflow: 'hidden' }}>
                                                        <Box
                                                            component="img"
                                                            src={item.image}
                                                            alt={item.title}
                                                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    </Box>
                                                    <Box sx={{ p: 3 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, height: '3.5rem', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', color: offBlack }}>
                                                            {item.title}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Button size="small" variant="outlined" onClick={() => handleEdit(item)} fullWidth>Editar</Button>
                                                            <Button size="small" variant="outlined" color="error" onClick={() => deleteNews(item.id)} fullWidth>Excluir</Button>
                                                        </Box>
                                                    </Box>
                                                </Paper>
                                            </SortableItem>
                                        </Box>
                                    ))}
                                </Box>
                            </SortableContext>
                        </DndContext>
                    </Box>

                    <Dialog
                        open={newsEditorOpen}
                        onClose={handleCancel}
                        fullScreen={isMobile}
                        fullWidth
                        maxWidth="lg"
                        PaperProps={{
                            sx: {
                                borderRadius: isMobile ? 0 : '24px',
                                overflow: 'hidden'
                            }
                        }}
                    >
                        <DialogTitle
                            sx={{
                                px: 3,
                                py: 2,
                                borderBottom: '1px solid rgba(0,0,0,0.08)',
                                backgroundColor: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2
                            }}
                        >
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: offBlack, lineHeight: 1.1 }}>
                                    {editingId ? 'Editar Notícia' : 'Nova Notícia'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: neutralGray, mt: 0.5 }}>
                                    Edite o texto com formatação e publique quando estiver pronto.
                                </Typography>
                            </Box>
                            <IconButton onClick={handleCancel} aria-label="Fechar" sx={{ color: '#666' }}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>

                        <DialogContent
                            dividers
                            sx={{
                                p: 3,
                                backgroundColor: '#fafafa'
                            }}
                        >
                            <Box
                                component="form"
                                id="news-editor-form"
                                onSubmit={handleSubmit}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '420px 1fr' },
                                    gap: 3,
                                    alignItems: 'start'
                                }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2.5,
                                        borderRadius: '16px',
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        backgroundColor: '#fff'
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: offBlack, mb: 2 }}>
                                        Metadados
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        label="Título"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        required
                                        sx={{ mb: 2.25 }}
                                    />

                                    <TextField
                                        fullWidth
                                        label="Subtítulo"
                                        name="subtitle"
                                        value={formData.subtitle}
                                        onChange={handleChange}
                                        sx={{ mb: 2.25 }}
                                        placeholder="Resumo curto para aparecer abaixo do título"
                                    />

                                    <Box sx={{ display: 'flex', gap: 2, mb: 2.25 }}>
                                        <TextField
                                            label="Autor"
                                            name="author"
                                            value={formData.author}
                                            onChange={handleChange}
                                            sx={{ flex: 1 }}
                                            placeholder="Ex: R5 Editorial"
                                        />
                                        <TextField
                                            label="Data de Publicação"
                                            name="publishedAt"
                                            type="date"
                                            value={formData.publishedAt}
                                            onChange={handleChange}
                                            sx={{ flex: 1 }}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Box>

                                    <ImageUpload
                                        label="Imagem da Notícia"
                                        folder="news/"
                                        currentImage={formData.image}
                                        onUploadingChange={setNewsImageUploading}
                                        onUpload={(data) => setFormData({ ...formData, image: data.url, imageMetadata: data })}
                                    />

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.published !== false}
                                                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                                                sx={{ color: brandRed, '&.Mui-checked': { color: brandRed } }}
                                            />
                                        }
                                        label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Publicado no Site</Typography>}
                                        sx={{ display: 'block', mt: 1.5 }}
                                    />
                                </Paper>

                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2.5,
                                        borderRadius: '16px',
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        backgroundColor: '#fff'
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: offBlack, mb: 2 }}>
                                        Conteúdo
                                    </Typography>

                                    <RichTextEditor
                                        label="Conteúdo"
                                        value={formData.content}
                                        onChange={(value) => setFormData({ ...formData, content: value })}
                                        minHeight={isMobile ? '340px' : '420px'}
                                        maxHeight={isMobile ? '55vh' : '60vh'}
                                    />

                                    <Box sx={{ mt: 2.5 }}>
                                        <Typography sx={{ fontWeight: 800, mb: 1.25, color: offBlack }}>
                                            Galeria (Opcional)
                                        </Typography>
                                        <MultiImageUpload
                                            images={formData.gallery || []}
                                            onChange={(newGallery) => setFormData({ ...formData, gallery: newGallery })}
                                        />
                                    </Box>
                                </Paper>
                            </Box>
                        </DialogContent>

                        <DialogActions
                            sx={{
                                px: 3,
                                py: 2,
                                borderTop: '1px solid rgba(0,0,0,0.08)',
                                backgroundColor: '#fff',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Button onClick={handleCancel} variant="outlined" sx={{ color: neutralGray, borderColor: '#ddd' }}>
                                Fechar
                            </Button>
                            <Button
                                type="submit"
                                form="news-editor-form"
                                variant="contained"
                                disabled={newsImageUploading}
                                sx={{
                                    backgroundColor: brandRed,
                                    '&:hover': { backgroundColor: '#CC0000' },
                                    borderRadius: '999px',
                                    px: 2.5
                                }}
                            >
                                {editingId ? 'Atualizar Notícia' : 'Publicar Notícia'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            )
            }

            {activeTab === 'content' && (
                <Box>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            mb: 6,
                            borderRadius: '24px',
                            border: '1px solid rgba(0,0,0,0.08)',
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, pb: 2, borderBottom: '1px solid #f0f0f0', color: offBlack }}>
                            {editingContentId ? 'Editar Item' : 'Adicionar Novo Item'}
                        </Typography>
                        <Box component="form" onSubmit={handleContentSubmit}>
                            <TextField
                                fullWidth
                                label="Título"
                                name="title"
                                value={contentFormData.title}
                                onChange={handleContentChange}
                                required
                                sx={{ mb: 3 }}
                            />
                            <TextField
                                fullWidth
                                label="Descrição"
                                name="description"
                                value={contentFormData.description}
                                onChange={handleContentChange}
                                multiline
                                rows={3}
                                required
                                sx={{ mb: 3 }}
                            />
                            <Typography variant="subtitle2" sx={{ mb: 1, color: '#666' }}>Texto do Artigo</Typography>
                            <Box sx={{ mb: 3 }}>
                                <RichTextEditor
                                    label="Texto do Artigo"
                                    value={contentFormData.body}
                                    onChange={(value) => setContentFormData({ ...contentFormData, body: value })}
                                    minHeight={isMobile ? '340px' : '460px'}
                                    maxHeight={isMobile ? '55vh' : '70vh'}
                                />
                            </Box>
                            <TextField
                                fullWidth
                                label="Autor"
                                name="author"
                                value={contentFormData.author}
                                onChange={handleContentChange}
                                sx={{ mb: 3 }}
                                placeholder="Nome do autor ou fonte"
                            />
                            <TextField
                                fullWidth
                                label="Etiqueta"
                                name="label"
                                value={contentFormData.label}
                                onChange={handleContentChange}
                                required
                                sx={{ mb: 3 }}
                            />
                            <ImageUpload
                                label="Imagem do Conteúdo"
                                folder="content/"
                                currentImage={contentFormData.image}
                                onUpload={(data) => setContentFormData({ ...contentFormData, image: data.url, imageMetadata: data })}
                            />
                            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                >
                                    {editingContentId ? 'Atualizar Item' : 'Publicar Item'}
                                </Button>
                                {editingContentId && (
                                    <Button onClick={handleContentCancel} variant="outlined" sx={{ color: neutralGray, borderColor: '#ddd' }}>
                                        Cancelar
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Paper>

                    <Box sx={{ mb: 8 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 4, color: offBlack }}>Itens de Conteúdo Atuais</Typography>
                        <Grid container spacing={3}>
                            {contents.map(item => (
                                <Grid item xs={12} sm={6} md={4} key={item.id}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            borderRadius: '24px',
                                            overflow: 'hidden',
                                            border: '1px solid #eaecf0',
                                            transition: 'all 0.3s ease',
                                            '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 12px 24px rgba(0,0,0,0.06)', borderColor: brandRed }
                                        }}
                                    >
                                        <Box sx={{ height: 180, overflow: 'hidden' }}>
                                            <Box
                                                component="img"
                                                src={item.image}
                                                alt={item.title}
                                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        </Box>
                                        <Box sx={{ p: 3 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: offBlack }}>{item.title}</Typography>
                                            <Typography variant="caption" sx={{ color: neutralGray, mb: 3, display: 'block', fontWeight: 500 }}>{item.label}</Typography>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button size="small" variant="outlined" onClick={() => handleContentEdit(item)} fullWidth>Editar</Button>
                                                <Button size="small" variant="outlined" color="error" onClick={() => deleteContent(item.id)} fullWidth>Excluir</Button>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Box>
            )}

            {activeTab === 'hero' && (
                /* HERO SECTION */
                <Box>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            mb: 6,
                            borderRadius: '24px',
                            border: '1px solid rgba(0,0,0,0.08)',
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, pb: 2, borderBottom: '1px solid #f0f0f0', color: offBlack }}>
                            Editar Destaques / Seção Hero
                        </Typography>
                        <Box sx={{ mb: 2.5 }}>
                            <Typography variant="caption" sx={{ color: neutralGray, display: 'block' }}>
                                Canal ativo: <strong>{String(contentChannel || '').toUpperCase()}</strong> | Chave de escrita: <strong>{contentKey}</strong>
                            </Typography>
                            <Typography variant="caption" sx={{ color: neutralGray, display: 'block' }}>
                                Fonte carregada: <strong>{loadedFromKey}</strong>{usingLegacyFallback ? ' (fallback legado)' : ''}
                            </Typography>
                        </Box>
                        <Box component="form" onSubmit={handleHeroUpdate}>
                            <Box sx={{ mb: 4, p: 4, backgroundColor: '#fcfcfc', borderRadius: '24px', border: '1px solid #f0f0f0' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: offBlack, letterSpacing: '0.05em' }}>
                                    CÓPIA E CONTEXTO
                                </Typography>
                                <Typography variant="caption" sx={{ color: neutralGray, display: 'block', mb: 4 }}>
                                    Defina os textos principais que serão exibidos no primeiro impacto do portal.
                                </Typography>

                                <Stack spacing={4}>
                                    <Box sx={{ maxWidth: '320px' }}>
                                        <TextField
                                            label="Etiqueta"
                                            name="tag"
                                            value={heroFormData.tag}
                                            onChange={handleHeroChange}
                                            placeholder="Ex: ESTRATÉGIA"
                                            helperText="Destaque pequeno acima do título"
                                        />
                                    </Box>

                                    <TextField
                                        label="Título do Showcase"
                                        name="title"
                                        value={heroFormData.title}
                                        onChange={handleHeroChange}
                                        placeholder="Ex: Inteligência Digital para Negócios de Escala"
                                        helperText="O título principal de maior impacto no Hero"
                                    />

                                    <TextField
                                        label="Resumo Editorial (Texto de Apoio)"
                                        name="summary"
                                        value={heroFormData.summary}
                                        onChange={handleHeroChange}
                                        multiline
                                        rows={10}
                                        placeholder="Escreva aqui a apresentação detalhada..."
                                        helperText="Este texto deve ser claro, atraente e vender a solução em poucos segundos."
                                    />
                                </Stack>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    startIcon={<SaveIcon />}
                                    sx={{ py: 1.5, px: 6 }}
                                >
                                    Salvar Alterações de Texto
                                </Button>
                            </Box>
                        </Box>
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 6,
                            borderRadius: '32px',
                            border: '1px solid rgba(0,0,0,0.06)',
                            backgroundColor: '#fff'
                        }}
                    >
                        <Box sx={{ mb: 6 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: offBlack, letterSpacing: '0.05em' }}>
                                CARROSSEL DE MÍDIA
                            </Typography>
                            <Typography variant="caption" sx={{ color: neutralGray, display: 'block', mb: 4 }}>
                                Gerencie o visual dinâmico do seu Hero. Recomendamos vídeos em MP4 ou imagens de alta resolução (1920x1080).
                            </Typography>

                            <Box sx={{ p: 4, backgroundColor: '#fcfcfc', borderRadius: '24px', border: '1px solid #f0f0f0' }}>
                                <Box component="form" onSubmit={handleAddMedia}>
                                    <Stack spacing={4}>
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 700, mb: 1.5, display: 'block', color: offBlack }}>
                                                1. SELECIONE O ARQUIVO
                                            </Typography>
                                            <ImageUpload
                                                label="Clique ou arraste para fazer upload"
                                                folder={mediaType === 'image' ? 'hero/' : 'videos/'}
                                                currentImage={mediaInput?.url || mediaInput}
                                                onUpload={(data) => setMediaInput(data)}
                                                accept={mediaType === 'image' ? 'image/*' : 'video/*'}
                                            />
                                        </Box>

                                        <Box sx={{ maxWidth: '400px' }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, mb: 1.5, display: 'block', color: offBlack }}>
                                                2. ESPECIFIQUE O FORMATO
                                            </Typography>
                                            <FormControl fullWidth>
                                                <InputLabel>Formato</InputLabel>
                                                <Select
                                                    value={mediaType}
                                                    label="Formato"
                                                    onChange={(e) => setMediaType(e.target.value)}
                                                >
                                                    <MenuItem value="image">Imagem Estática (JPG/PNG/WEBP)</MenuItem>
                                                    <MenuItem value="video">Vídeo de Fundo (MP4)</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2, borderTop: '1px solid #f0f0f0' }}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                color="primary"
                                                startIcon={<SaveIcon />}
                                                sx={{ px: 6, py: 1.5 }}
                                            >
                                                Adicionar ao Carrossel
                                            </Button>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Box>
                        </Box>

                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, mb: 4 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: offBlack, textTransform: 'none' }}>Ativos Carregados</Typography>
                                <Typography variant="caption" sx={{ color: neutralGray, mb: 0.5 }}>({heroData.media?.length || 0})</Typography>
                            </Box>

                            {Array.isArray(heroData.media) && heroData.media.length > 1 && (
                                <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={async () => {
                                            const items = Array.isArray(heroData.media) ? heroData.media : [];
                                            const lastVideo = (() => {
                                                for (let i = items.length - 1; i >= 0; i--) {
                                                    const it = items[i];
                                                    if (it?.type === 'video' && it?.url) return it;
                                                }
                                                return null;
                                            })();

                                            if (!lastVideo) {
                                                showToast('Nenhum vídeo encontrado no Hero.');
                                                return;
                                            }

                                            await updateHeroData({ ...heroData, media: [lastVideo] });
                                            showToast('Hero atualizado: mantido apenas o vídeo.');
                                        }}
                                        sx={{ fontWeight: 700, borderRadius: '14px', px: 2.2, py: 1 }}
                                    >
                                        Manter apenas o vídeo
                                    </Button>

                                    <Button
                                        size="small"
                                        color="error"
                                        variant="text"
                                        onClick={async () => {
                                            const items = Array.isArray(heroData.media) ? heroData.media : [];
                                            const onlyImages = items.filter((it) => it?.type === 'image');
                                            await updateHeroData({ ...heroData, media: onlyImages });
                                            showToast('Hero atualizado: removidos os vídeos (mantidas apenas imagens).');
                                        }}
                                        sx={{ fontWeight: 700, borderRadius: '14px', px: 1.8, py: 1 }}
                                    >
                                        Remover vídeos
                                    </Button>
                                </Box>
                            )}

                            <Grid container spacing={4}>
                                {heroData.media && heroData.media.map((item, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                borderRadius: '24px',
                                                overflow: 'hidden',
                                                border: '1px solid #f0f0f0',
                                                transition: 'all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)',
                                                '&:hover': {
                                                    boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                                                    borderColor: brandRed,
                                                    transform: 'scale(1.02)'
                                                }
                                            }}
                                        >
                                            <Box sx={{ height: 140, backgroundColor: '#000', position: 'relative' }}>
                                                {item.type === 'image' ? (
                                                    <Box component="img" src={item.url} sx={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.9 }} />
                                                ) : (
                                                    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(45deg, #000, #222)' }}>
                                                        <PlayCircleOutlineIcon sx={{ color: 'white', fontSize: '2.5rem', opacity: 0.5 }} />
                                                    </Box>
                                                )}
                                                <Box sx={{ position: 'absolute', top: 12, right: 12, bgcolor: brandRed, color: 'white', px: 1, py: 0.2, borderRadius: '4px', fontSize: '0.65rem', fontWeight: 800 }}>
                                                    SLIDE {index + 1}
                                                </Box>
                                            </Box>
                                            <Box sx={{ p: 2.5, backgroundColor: '#fff' }}>
                                                <Typography variant="caption" sx={{ color: neutralGray, display: 'block', mb: 2.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {item.url.split('/').pop()}
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    variant="text"
                                                    fullWidth
                                                    onClick={() => removeMedia(item.id)}
                                                    sx={{ borderRadius: '14px', fontWeight: 700, fontSize: '0.7rem' }}
                                                >
                                                    Remover Ativo
                                                </Button>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Paper>
                </Box>
            )}
            {activeTab === 'impact' && (
                <Box>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: '24px',
                            border: '1px solid rgba(0,0,0,0.08)',
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, pb: 2, borderBottom: '1px solid #f0f0f0', color: offBlack }}>
                            Edit 'Ciência & Tecnologia' (Impact Section)
                        </Typography>
                        <Box component="form" onSubmit={(e) => {
                            e.preventDefault();
                            updateImpactData(impactForm);
                            showToast('Seção Impacto atualizada com sucesso!');
                        }}>
                            <Box sx={{ mb: 4, p: 4, backgroundColor: '#fcfcfc', borderRadius: '24px', border: '1px solid #f0f0f0' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: offBlack, letterSpacing: '0.05em' }}>
                                    CONTEÚDO EDITORIAL
                                </Typography>
                                <Typography variant="caption" sx={{ color: neutralGray, display: 'block', mb: 4 }}>
                                    Defina a mensagem de impacto que aparece após a seção de cases.
                                </Typography>

                                <Stack spacing={4}>
                                    <Box sx={{ maxWidth: '400px' }}>
                                        <TextField
                                            fullWidth
                                            label="Título da Seção (Pequeno)"
                                            value={impactForm.sectionTitle}
                                            onChange={(e) => setImpactForm({ ...impactForm, sectionTitle: e.target.value })}
                                            placeholder="Ex: CIÊNCIA & TECNOLOGIA"
                                            helperText="Texto que aparece acima da manchete"
                                        />
                                    </Box>

                                    <TextField
                                        fullWidth
                                        label="Manchete Principal de Impacto"
                                        value={impactForm.title}
                                        onChange={(e) => setImpactForm({ ...impactForm, title: e.target.value })}
                                        placeholder="Ex: Criando um impacto que reverbera..."
                                        helperText="O texto de maior destaque desta seção"
                                    />

                                    <TextField
                                        fullWidth
                                        label="Descrição Detalhada"
                                        multiline
                                        rows={8}
                                        value={impactForm.description}
                                        onChange={(e) => setImpactForm({ ...impactForm, description: e.target.value })}
                                        placeholder="Descreva o propósito ou a visão..."
                                        helperText="Corpo de texto principal da seção de impacto"
                                    />

                                    <Box sx={{ pt: 2, borderTop: '1px solid #f0f0f0' }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, mb: 2, display: 'block', color: offBlack }}>
                                            IMAGEM DE SUPORTE
                                        </Typography>
                                        <ImageUpload
                                            label="Upload da Imagem de Impacto"
                                            folder="impact/"
                                            currentImage={impactForm.image}
                                            onUpload={(data) => setImpactForm({ ...impactForm, image: data.url, imageMetadata: data })}
                                        />
                                    </Box>
                                </Stack>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    startIcon={<SaveIcon />}
                                    sx={{ py: 1.5, px: 6 }}
                                >
                                    Salvar Alterações de Impacto
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            )}


            {activeTab === 'cases' && (
                <Box>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            mb: 6,
                            borderRadius: '24px',
                            border: '1px solid rgba(0,0,0,0.08)',
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, pb: 2, borderBottom: '1px solid #f0f0f0', color: offBlack }}>
                            {editingCaseId ? 'Editar Caso de Sucesso' : 'Adicionar Novo Caso'}
                        </Typography>
                        <Box component="form" onSubmit={handleCaseSubmit}>
                            <Box sx={{ p: 4, backgroundColor: '#fcfcfc', borderRadius: '24px', border: '1px solid #f0f0f0', mb: 4 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: offBlack, letterSpacing: '0.05em' }}>
                                    IDENTIDADE DO CASO
                                </Typography>
                                <Typography variant="caption" sx={{ color: neutralGray, display: 'block', mb: 4 }}>
                                    Forneça as informações base do estudo de caso para exibição no portal.
                                </Typography>

                                <Stack spacing={4}>
                                    <TextField
                                        fullWidth
                                        label="Título do Caso"
                                        value={caseForm.title}
                                        onChange={(e) => setCaseForm({ ...caseForm, title: e.target.value })}
                                        required
                                        placeholder="Ex: Transformação Digital na Indústria 4.0"
                                        helperText="O título que aparecerá no card do caso"
                                    />

                                    <Grid container spacing={4}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Data de Publicação"
                                                type="date"
                                                InputLabelProps={{ shrink: true }}
                                                value={formatDateForInput(caseForm.date)}
                                                onChange={(e) => setCaseForm({ ...caseForm, date: e.target.value })}
                                                required
                                                helperText="Data para ordenação cronológica"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Etiqueta / Categoria"
                                                value={caseForm.label}
                                                onChange={(e) => setCaseForm({ ...caseForm, label: e.target.value })}
                                                required
                                                placeholder="Ex: ESTUDO DE CASO"
                                                helperText="Destaque pequeno acima da imagem"
                                            />
                                        </Grid>
                                    </Grid>

                                    <Box sx={{ pt: 2, borderTop: '1px solid #f0f0f0' }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, mb: 2, display: 'block', color: offBlack }}>
                                            VISUAL E NARRATIVA
                                        </Typography>
                                        <Stack spacing={4}>
                                            <ImageUpload
                                                label="Upload da Imagem de Capa"
                                                folder="cases/"
                                                currentImage={caseForm.image}
                                                onUpload={(data) => setCaseForm({ ...caseForm, image: data.url, imageMetadata: data })}
                                            />
                                            <Box sx={{ p: 2, border: '1px dashed #e0e0e0', borderRadius: 2, bgcolor: '#fafafa' }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1, color: offBlack }}>Galeria de Imagens (Até 4)</Typography>
                                                <Typography variant="caption" sx={{ display: 'block', mb: 2, color: neutralGray }}>
                                                    Adicione imagens extras para detalhar o case. Elas aparecerão abaixo da imagem principal.
                                                </Typography>
                                                <MultiImageUpload
                                                    images={caseForm.gallery || []}
                                                    onChange={(newGallery) => setCaseForm({ ...caseForm, gallery: newGallery })}
                                                    maxImages={4}
                                                />
                                            </Box>
                                            <TextField
                                                fullWidth
                                                label="Descrição do Caso"
                                                multiline
                                                rows={10}
                                                value={caseForm.description}
                                                onChange={(e) => setCaseForm({ ...caseForm, description: e.target.value })}
                                                required
                                                placeholder="Descreva o desafio, a solução e os resultados alcançados..."
                                                helperText="Use este espaço para contar a história do sucesso."
                                            />
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                {editingCaseId && (
                                    <Button onClick={handleCaseCancel} variant="outlined" sx={{ px: 4, py: 1.5, color: neutralGray, borderColor: '#ddd' }}>
                                        Cancelar Edição
                                    </Button>
                                )}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    startIcon={editingCaseId ? <SaveIcon /> : <SaveIcon />}
                                    sx={{ py: 1.5, px: 6 }}
                                >
                                    {editingCaseId ? 'Salvar Caso Atualizado' : 'Adicionar Novo Caso'}
                                </Button>
                            </Box>
                        </Box>
                    </Paper>

                    <Box sx={{ mb: 8 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 4, color: offBlack }}>Casos de Sucesso Atuais</Typography>
                        <Grid container spacing={3}>
                            {cases.map(item => (
                                <Grid item xs={12} sm={6} md={4} key={item.id}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            borderRadius: '24px',
                                            overflow: 'hidden',
                                            border: editingCaseId === item.id ? `2px solid ${brandRed}` : '1px solid #eaecf0',
                                            backgroundColor: editingCaseId === item.id ? '#fffafb' : '#fff',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 40px rgba(0,0,0,0.08)', borderColor: brandRed }
                                        }}
                                    >
                                        <Box sx={{ height: 200, overflow: 'hidden', position: 'relative' }}>
                                            <Box
                                                component="img"
                                                src={item.image}
                                                alt={item.title}
                                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                            <Box sx={{ position: 'absolute', top: 16, right: 16, backgroundColor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(4px)', px: 1.5, py: 0.5, borderRadius: '14px' }}>
                                                <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: offBlack }}>{item.label || 'CASE'}</Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ p: 3 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: offBlack, lineHeight: 1.3 }}>{item.title}</Typography>
                                            <Typography variant="caption" sx={{ color: neutralGray, mb: 3, display: 'block', fontWeight: 500, letterSpacing: '0.02em' }}>{item.date}</Typography>

                                            <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => handleCaseEdit(item)}
                                                    fullWidth
                                                    disabled={editingCaseId === item.id}
                                                    sx={{ py: 1, borderRadius: '14px' }}
                                                >
                                                    {editingCaseId === item.id ? 'Editando...' : 'Editar'}
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => deleteCase(item.id)}
                                                    fullWidth
                                                    sx={{ py: 1, borderRadius: '14px' }}
                                                >
                                                    Excluir
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Box>
            )}

            {activeTab === 'engines' && (
                <Box>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            mb: 4,
                            borderRadius: '24px',
                            border: '1px solid rgba(0,0,0,0.08)',
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'flex-end' } }}>
                            <TextField
                                fullWidth
                                label="Título da Seção"
                                value={enginesForm.sectionTitle || ''}
                                onChange={(e) => handleEnginesSectionTitleChange(e.target.value)}
                                helperText="Ex: Nossos Motores de Desenvolvimento"
                            />
                            <Button
                                variant="contained"
                                onClick={handleSaveDevelopmentEngines}
                                startIcon={<SaveIcon />}
                                sx={{ py: 1.5, px: 4, whiteSpace: 'nowrap' }}
                            >
                                Salvar Seção
                            </Button>
                        </Box>
                    </Paper>

                    <Grid container spacing={3}>
                        {(enginesForm.engines || []).map((engine, engineIndex) => (
                            <Grid item xs={12} md={6} key={engine.id || engine.number || engineIndex}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        borderRadius: '20px',
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        backgroundColor: '#fff',
                                        height: '100%'
                                    }}
                                >
                                    <Typography variant="overline" sx={{ color: neutralGray, fontWeight: 700 }}>
                                        MOTOR {engine.number || `${engineIndex + 1}`.padStart(2, '0')}
                                    </Typography>

                                    <Stack spacing={2} sx={{ mt: 1 }}>
                                        <TextField
                                            fullWidth
                                            label="Título"
                                            value={engine.title || ''}
                                            onChange={(e) => handleEngineFieldChange(engineIndex, 'title', e.target.value)}
                                        />

                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={5}
                                            label="Texto Central"
                                            value={engine.description || ''}
                                            onChange={(e) => handleEngineFieldChange(engineIndex, 'description', e.target.value)}
                                        />

                                        {[0, 1, 2].map((capabilityIndex) => (
                                            <TextField
                                                key={`engine-${engineIndex}-cap-${capabilityIndex}`}
                                                fullWidth
                                                label={`Bullet ${capabilityIndex + 1}`}
                                                value={(engine.capabilities && engine.capabilities[capabilityIndex]) || ''}
                                                onChange={(e) => handleEngineCapabilityChange(engineIndex, capabilityIndex, e.target.value)}
                                            />
                                        ))}
                                    </Stack>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                        <Button
                            variant="contained"
                            onClick={handleSaveDevelopmentEngines}
                            startIcon={<SaveIcon />}
                            sx={{ py: 1.5, px: 6 }}
                        >
                            Salvar Motores de Desenvolvimento
                        </Button>
                    </Box>
                </Box>
            )}

            {activeTab === 'legal' && (
                <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
                        {/* SIDEBAR */}
                        <Box sx={{ width: { xs: '100%', md: '300px' }, flexShrink: 0, position: { md: 'sticky' }, top: 20 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: '24px',
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    backgroundColor: '#fff'
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 3, color: offBlack, px: 1, letterSpacing: '0.05em' }}>
                                    DOCUMENTOS
                                </Typography>

                                <Stack spacing={1}>
                                    {/* LEGAL CATEGORY */}
                                    {legalDocuments.filter(d => d.category === 'legal').map(doc => (
                                        <Button
                                            key={doc.id}
                                            fullWidth
                                            variant={selectedDocId === doc.id ? 'contained' : 'text'}
                                            onClick={() => setSelectedDocId(doc.id)}
                                            sx={{
                                                justifyContent: 'flex-start',
                                                borderRadius: '12px',
                                                py: 1.5,
                                                px: 2,
                                                textTransform: 'none',
                                                backgroundColor: selectedDocId === doc.id ? brandRed : 'transparent',
                                                color: selectedDocId === doc.id ? 'white' : neutralGray,
                                                '&:hover': { backgroundColor: selectedDocId === doc.id ? brandRed : '#f5f5f5' }
                                            }}
                                            startIcon={doc.slug.includes('privacy') ? <ShieldIcon /> : <GavelIcon />}
                                        >
                                            {doc.title}
                                        </Button>
                                    ))}

                                    <Divider sx={{ my: 2 }} />

                                    <Typography variant="caption" sx={{ fontWeight: 700, color: neutralGray, mb: 1.5, px: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Governança & Compliance
                                    </Typography>

                                    {/* GOVERNANCE CATEGORY */}
                                    {legalDocuments.filter(d => d.category === 'governance').map(doc => (
                                        <Button
                                            key={doc.id}
                                            fullWidth
                                            variant={selectedDocId === doc.id ? 'contained' : 'text'}
                                            onClick={() => setSelectedDocId(doc.id)}
                                            sx={{
                                                justifyContent: 'flex-start',
                                                borderRadius: '12px',
                                                py: 1,
                                                px: 2,
                                                textAlign: 'left',
                                                textTransform: 'none',
                                                fontSize: '0.85rem',
                                                whiteSpace: 'normal',
                                                backgroundColor: selectedDocId === doc.id ? brandRed : 'transparent',
                                                color: selectedDocId === doc.id ? 'white' : neutralGray,
                                                '&:hover': { backgroundColor: selectedDocId === doc.id ? brandRed : '#f5f5f5' }
                                            }}
                                            startIcon={<EngineeringIcon sx={{ fontSize: '1.2rem' }} />}
                                        >
                                            {doc.title}
                                        </Button>
                                    ))}

                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<Typography sx={{ fontSize: '1.2rem', lineHeight: 1 }}>+</Typography>}
                                        onClick={() => setSelectedDocId('new')}
                                        sx={{
                                            mt: 2,
                                            borderStyle: 'dashed',
                                            color: neutralGray,
                                            borderColor: '#ddd',
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            '&:hover': { borderColor: brandRed, color: brandRed, backgroundColor: '#fff' }
                                        }}
                                    >
                                        Criar Novo Documento
                                    </Button>
                                </Stack>
                            </Paper>
                        </Box>

                        {/* EDITOR AREA */}
                        <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 4,
                                    borderRadius: '24px',
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    backgroundColor: '#fff'
                                }}
                            >
                                <Box sx={{ width: '100%', mb: 2 }}>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                                        <TextField
                                            fullWidth
                                            label="Título do Documento"
                                            value={legalForm.title}
                                            onChange={(e) => handleLegalFormChange('title', e.target.value)}
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    fontWeight: 700,
                                                    fontSize: '1.1rem',
                                                    backgroundColor: '#f9f9f9'
                                                }
                                            }}
                                        />
                                    </Box>

                                    {/* Meta info editing (slug/category) - show only if editing governance or creating new */}
                                    {(selectedDocId === 'new' || (legalForm.category === 'governance')) && (
                                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                            <TextField
                                                label="Slug (URL)"
                                                value={legalForm.slug}
                                                onChange={(e) => handleLegalFormChange('slug', e.target.value)}
                                                size="small"
                                                fullWidth
                                                helperText="Identificador único (ex: codigo-etica)"
                                                disabled={['terms-of-use', 'privacy-policy'].includes(legalForm.slug)}
                                            />
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Categoria</InputLabel>
                                                <Select
                                                    value={legalForm.category}
                                                    label="Categoria"
                                                    onChange={(e) => handleLegalFormChange('category', e.target.value)}
                                                    disabled={['terms-of-use', 'privacy-policy'].includes(legalForm.slug)}
                                                >
                                                    <MenuItem value="legal">Legal</MenuItem>
                                                    <MenuItem value="governance">Governança</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    )}

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="caption" sx={{ color: neutralGray }}>
                                            Edite o conteúdo formatado do documento abaixo.
                                        </Typography>

                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            {selectedDocId !== 'new' && !['terms-of-use', 'privacy-policy'].includes(legalForm.slug) && (
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={handleDeleteLegal}
                                                    startIcon={<DeleteIcon />}
                                                    sx={{ borderRadius: '14px', px: 2 }}
                                                >
                                                    Excluir
                                                </Button>
                                            )}

                                            <Button
                                                variant="contained"
                                                onClick={handleSaveLegal}
                                                startIcon={<SaveIcon />}
                                                sx={{ borderRadius: '14px', px: 4, whiteSpace: 'nowrap' }}
                                            >
                                                Salvar Alterações
                                            </Button>
                                        </Box>
                                    </Box>
                                </Box>

                                <Box sx={{ minHeight: '500px', width: '100%', overflow: 'hidden' }}>
                                    <RichTextEditor
                                        label="Conteúdo"
                                        value={legalForm.content}
                                        onChange={(val) => handleLegalFormChange('content', val)}
                                        minHeight={isMobile ? '380px' : '520px'}
                                        maxHeight={isMobile ? '60vh' : '70vh'}
                                        key={selectedDocId} // Force re-render on doc switch
                                    />
                                </Box>

                                <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #f0f0f0' }}>
                                    <Typography variant="caption" sx={{ color: neutralGray, fontStyle: 'italic' }}>
                                        Dica: Use as ferramentas de formatação no topo do editor para listas, negrito e links.
                                    </Typography>
                                </Box>
                            </Paper>
                        </Box>
                    </Box>
                </Box>
            )}

            {activeTab === 'executivePanel' && (
                <Box>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2.5, md: 3.2 },
                            mb: 3,
                            borderRadius: '28px',
                            border: '1px solid rgba(15,23,42,0.08)',
                            background: 'linear-gradient(135deg, #111827 0%, #1f2937 42%, #334155 100%)',
                            color: '#fff',
                            boxShadow: '0 18px 42px rgba(15,23,42,0.18)'
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 2.4, alignItems: { xs: 'flex-start', lg: 'center' }, flexDirection: { xs: 'column', lg: 'row' } }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.72)', letterSpacing: '0.08em', fontWeight: 700 }}>
                                    Direção do Portal
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', mb: 0.8 }}>
                                    Painel Executivo
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.78)', maxWidth: '780px' }}>
                                    Leitura consolidada de tráfego, SEO, conversão e expansão regional para tomada de decisão mais rápida.
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip label={`Janela: ${internalOverviewData?.period?.days || internalOverviewDays} dias`} sx={{ backgroundColor: 'rgba(255,255,255,0.12)', color: '#fff', borderRadius: '999px' }} />
                                <Chip label={`SEO: ${Number(seoSummary?.healthScore || 0)} / 100`} sx={{ backgroundColor: 'rgba(34,197,94,0.16)', color: '#dcfce7', borderRadius: '999px', fontWeight: 700 }} />
                                <Chip label={`Orgânico: ${formatPercent(organicShare)}`} sx={{ backgroundColor: 'rgba(56,189,248,0.16)', color: '#dbeafe', borderRadius: '999px', fontWeight: 700 }} />
                            </Box>
                        </Box>
                    </Paper>

                    {(internalOverviewLoading && !internalOverviewData) || (seoHealthLoading && !seoHealthData) ? (
                        <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box sx={{ display: 'grid', gap: 2.5 }}>
                            {(internalOverviewError || seoHealthError) && (
                                <Alert severity="error">
                                    {internalOverviewError || seoHealthError}
                                </Alert>
                            )}

                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        sm: 'repeat(2, minmax(0, 1fr))',
                                        xl: 'repeat(4, minmax(0, 1fr))'
                                    }
                                }}
                            >
                                {executiveHeadlineCards.map((item) => (
                                    <Paper
                                        key={item.label}
                                        elevation={0}
                                        sx={{
                                            p: 2.1,
                                            borderRadius: '20px',
                                            border: '1px solid rgba(15,23,42,0.08)',
                                            backgroundColor: '#fff',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <Box sx={{ position: 'absolute', inset: 0, opacity: 0.08, background: item.accent }} />
                                        <Box sx={{ position: 'relative' }}>
                                            <Typography variant="caption" sx={{ color: neutralGray, fontWeight: 700, letterSpacing: '0.06em' }}>
                                                {item.label}
                                            </Typography>
                                            <Typography variant="h4" sx={{ color: offBlack, fontWeight: 800, mt: 0.9, mb: 0.4 }}>
                                                {formatInt(item.value)}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: neutralGray }}>
                                                {item.note}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                ))}
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2.5,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        xl: '1.2fr 0.9fr 0.9fr'
                                    }
                                }}
                            >
                                <Paper elevation={0} sx={{ p: 2.5, borderRadius: '24px', border: '1px solid rgba(15,23,42,0.08)', backgroundColor: '#fff' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.6 }}>
                                        <Box>
                                            <Typography variant="overline" sx={{ color: neutralGray, letterSpacing: '0.08em' }}>
                                                Aquisição
                                            </Typography>
                                            <Typography variant="h6" sx={{ color: offBlack, fontWeight: 800 }}>
                                                Mix de Origem Limpo
                                            </Typography>
                                        </Box>
                                        <Chip label={`${formatPercent(directShare)} direto`} sx={{ borderRadius: '999px', backgroundColor: '#f8fafc', color: offBlack, fontWeight: 700 }} />
                                    </Box>
                                    <Box sx={{ display: 'grid', gap: 1.25 }}>
                                        {sourceGroups.length === 0 && (
                                            <Typography variant="body2" sx={{ color: neutralGray }}>
                                                Ainda sem dados suficientes para esta janela.
                                            </Typography>
                                        )}
                                        {sourceGroups.map((item) => (
                                            <Box key={`executive-source-${item.label}`}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.45 }}>
                                                    <Typography variant="body2" sx={{ color: offBlack, fontWeight: 600 }}>{item.label}</Typography>
                                                    <Typography variant="body2" sx={{ color: neutralGray }}>{formatInt(item.numericValue)}</Typography>
                                                </Box>
                                                <Box sx={{ height: 10, borderRadius: '999px', backgroundColor: '#edf2f7', overflow: 'hidden' }}>
                                                    <Box sx={{ width: item.width, height: '100%', background: item.label === 'Busca orgânica' ? 'linear-gradient(90deg,#2563eb,#38bdf8)' : item.label === 'Direto' ? 'linear-gradient(90deg,#111827,#334155)' : item.label === 'Social / Comunidades' ? 'linear-gradient(90deg,#7c3aed,#a855f7)' : 'linear-gradient(90deg,#94a3b8,#cbd5e1)' }} />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Paper>

                                <Paper elevation={0} sx={{ p: 2.5, borderRadius: '24px', border: '1px solid rgba(15,23,42,0.08)', backgroundColor: '#fff' }}>
                                    <Typography variant="overline" sx={{ color: neutralGray, letterSpacing: '0.08em' }}>
                                        SEO
                                    </Typography>
                                    <Typography variant="h6" sx={{ color: offBlack, fontWeight: 800, mb: 1.6 }}>
                                        Saúde Técnica
                                    </Typography>
                                    <Box sx={{ display: 'grid', gap: 1.1 }}>
                                        <Paper elevation={0} sx={{ p: 1.5, borderRadius: '16px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                            <Typography variant="caption" sx={{ color: neutralGray, fontWeight: 700, display: 'block' }}>Score de saúde</Typography>
                                            <Typography variant="h4" sx={{ color: seoStatusColor, fontWeight: 800 }}>{Number(seoSummary?.healthScore || 0)}</Typography>
                                        </Paper>
                                        <Paper elevation={0} sx={{ p: 1.5, borderRadius: '16px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                            <Typography variant="body2" sx={{ color: offBlack, fontWeight: 700 }}>{seoStatusLabel}</Typography>
                                            <Typography variant="caption" sx={{ color: neutralGray }}>
                                                {Number(seoSummary?.passedChecks || 0)} de {Number(seoSummary?.totalChecks || 0)} checks aprovados
                                            </Typography>
                                        </Paper>
                                        <Typography variant="body2" sx={{ color: neutralGray }}>
                                            Domínio canônico: <strong style={{ color: offBlack }}>{seoHealthData?.config?.canonicalHost || 'r5id.com.br'}</strong>
                                        </Typography>
                                    </Box>
                                </Paper>

                                <Paper elevation={0} sx={{ p: 2.5, borderRadius: '24px', border: '1px solid rgba(15,23,42,0.08)', backgroundColor: '#fff' }}>
                                    <Typography variant="overline" sx={{ color: neutralGray, letterSpacing: '0.08em' }}>
                                        Expansão Regional
                                    </Typography>
                                    <Typography variant="h6" sx={{ color: offBlack, fontWeight: 800, mb: 1.6 }}>
                                        Fora do DF
                                    </Typography>
                                    <Box sx={{ display: 'grid', gap: 1.2 }}>
                                        <Paper elevation={0} sx={{ p: 1.4, borderRadius: '16px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                            <Typography variant="caption" sx={{ color: neutralGray, fontWeight: 700, display: 'block' }}>Participação fora do DF</Typography>
                                            <Typography variant="h4" sx={{ color: offBlack, fontWeight: 800 }}>{formatPercent(regionalFocus?.nonDfShare || 0)}</Typography>
                                        </Paper>
                                        <Paper elevation={0} sx={{ p: 1.4, borderRadius: '16px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                            <Typography variant="caption" sx={{ color: neutralGray, fontWeight: 700, display: 'block' }}>São Paulo</Typography>
                                            <Typography variant="h4" sx={{ color: offBlack, fontWeight: 800 }}>{formatPercent(regionalFocus?.saoPauloShare || 0)}</Typography>
                                        </Paper>
                                        <Typography variant="body2" sx={{ color: neutralGray }}>
                                            Social/Comunidades já responde por {formatPercent(socialShare)} da aquisição limpa.
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2.5,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        xl: '1.1fr 0.9fr'
                                    }
                                }}
                            >
                                <Paper elevation={0} sx={{ p: 2.5, borderRadius: '24px', border: '1px solid rgba(15,23,42,0.08)', backgroundColor: '#fff' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.6 }}>
                                        <Box>
                                            <Typography variant="overline" sx={{ color: neutralGray, letterSpacing: '0.08em' }}>
                                                Conversão
                                            </Typography>
                                            <Typography variant="h6" sx={{ color: offBlack, fontWeight: 800 }}>
                                                Funil de Contato
                                            </Typography>
                                        </Box>
                                        <Chip label={`${formatPercent(leadConversionRate)} lead form`} sx={{ borderRadius: '999px', backgroundColor: '#fff7ed', color: '#c2410c', fontWeight: 700 }} />
                                    </Box>
                                    <Box sx={{ display: 'grid', gap: 1.2, gridTemplateColumns: { xs: '1fr', md: 'repeat(5, minmax(0, 1fr))' }, mb: 1.8 }}>
                                        {funnelSteps.map((step) => (
                                            <Paper key={`executive-funnel-${step.key}`} elevation={0} sx={{ p: 1.35, borderRadius: '16px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                <Typography variant="caption" sx={{ color: neutralGray, fontWeight: 700, display: 'block', minHeight: 30 }}>
                                                    {step.label}
                                                </Typography>
                                                <Typography variant="h5" sx={{ color: offBlack, fontWeight: 800, mt: 0.8 }}>
                                                    {formatInt(step.value)}
                                                </Typography>
                                            </Paper>
                                        ))}
                                    </Box>
                                    <Stack spacing={1}>
                                        {funnelInsights.slice(0, 4).map((item) => (
                                            <Paper key={item} elevation={0} sx={{ p: 1.3, borderRadius: '16px', backgroundColor: '#fff', border: '1px dashed #d0d5dd' }}>
                                                <Typography variant="body2" sx={{ color: neutralGray }}>
                                                    {item}
                                                </Typography>
                                            </Paper>
                                        ))}
                                    </Stack>
                                </Paper>

                                <Paper elevation={0} sx={{ p: 2.5, borderRadius: '24px', border: '1px solid rgba(15,23,42,0.08)', backgroundColor: '#fff' }}>
                                    <Typography variant="overline" sx={{ color: neutralGray, letterSpacing: '0.08em' }}>
                                        Prioridades
                                    </Typography>
                                    <Typography variant="h6" sx={{ color: offBlack, fontWeight: 800, mb: 1.6 }}>
                                        Próximos Passos Recomendados
                                    </Typography>
                                    <Stack spacing={1.1}>
                                        {executivePriorityItems.map((item, index) => (
                                            <Paper key={item.title} elevation={0} sx={{ p: 1.45, borderRadius: '16px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                <Typography variant="caption" sx={{ color: brandRed, fontWeight: 800, letterSpacing: '0.06em' }}>
                                                    PRIORIDADE {index + 1}
                                                </Typography>
                                                <Typography variant="body1" sx={{ color: offBlack, fontWeight: 700, mt: 0.35 }}>
                                                    {item.title}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: neutralGray, mt: 0.45 }}>
                                                    {item.detail}
                                                </Typography>
                                            </Paper>
                                        ))}
                                    </Stack>
                                </Paper>
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2.5,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        xl: '1fr 1fr'
                                    }
                                }}
                            >
                                <Paper elevation={0} sx={{ p: 2.5, borderRadius: '24px', border: '1px solid rgba(15,23,42,0.08)', backgroundColor: '#fff' }}>
                                    <Typography variant="overline" sx={{ color: neutralGray, letterSpacing: '0.08em' }}>
                                        Interesse
                                    </Typography>
                                    <Typography variant="h6" sx={{ color: offBlack, fontWeight: 800, mb: 1.6 }}>
                                        Páginas com Maior Tração
                                    </Typography>
                                    <Stack spacing={1.1}>
                                        {topPages.slice(0, 5).map((item) => (
                                            <Box key={`executive-page-${item.label}`}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.45 }}>
                                                    <Typography variant="body2" sx={{ color: offBlack, fontWeight: 600 }}>{item.label}</Typography>
                                                    <Typography variant="body2" sx={{ color: neutralGray }}>{formatInt(item.numericValue)}</Typography>
                                                </Box>
                                                <Box sx={{ height: 10, borderRadius: '999px', backgroundColor: '#edf2f7', overflow: 'hidden' }}>
                                                    <Box sx={{ width: item.width, height: '100%', background: 'linear-gradient(90deg,#111827,#475467)' }} />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Paper>

                                <Paper elevation={0} sx={{ p: 2.5, borderRadius: '24px', border: '1px solid rgba(15,23,42,0.08)', backgroundColor: '#fff' }}>
                                    <Typography variant="overline" sx={{ color: neutralGray, letterSpacing: '0.08em' }}>
                                        Geografia
                                    </Typography>
                                    <Typography variant="h6" sx={{ color: offBlack, fontWeight: 800, mb: 1.6 }}>
                                        UFs e Cidades Mais Ativas
                                    </Typography>
                                    <Box sx={{ display: 'grid', gap: 1.4, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: neutralGray, fontWeight: 700, display: 'block', mb: 0.8 }}>
                                                Top UFs
                                            </Typography>
                                            <Stack spacing={1}>
                                                {topStates.slice(0, 5).map((item) => (
                                                    <Box key={`executive-state-${item.label}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                                        <Typography variant="body2" sx={{ color: offBlack }}>{item.label}</Typography>
                                                        <Typography variant="body2" sx={{ color: neutralGray, fontWeight: 700 }}>{formatInt(item.numericValue)}</Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: neutralGray, fontWeight: 700, display: 'block', mb: 0.8 }}>
                                                Top Cidades
                                            </Typography>
                                            <Stack spacing={1}>
                                                {topCities.slice(0, 5).map((item) => (
                                                    <Box key={`executive-city-${item.label}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                                        <Typography variant="body2" sx={{ color: offBlack }}>{item.label}</Typography>
                                                        <Typography variant="body2" sx={{ color: neutralGray, fontWeight: 700 }}>{formatInt(item.numericValue)}</Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            {activeTab === 'internalBase' && (
                <Box>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2.5, md: 3.2 },
                            mb: 3,
                            borderRadius: '24px',
                            border: '1px solid rgba(0,0,0,0.08)',
                            background: 'linear-gradient(140deg, #ffffff 0%, #f8fafc 48%, #f1f5f9 100%)',
                            boxShadow: '0 10px 28px rgba(15,23,42,0.08)'
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 2.2, alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' } }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="overline" sx={{ color: neutralGray, letterSpacing: '0.08em' }}>
                                    Analytics Interno
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: offBlack, mb: 0.55 }}>
                                    Analytics
                                </Typography>
                                <Typography variant="body2" sx={{ color: neutralGray, maxWidth: '760px' }}>
                                    Radar de performance com dados first-party do portal: aquisição, engajamento, conversão e contatos.
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                {[7, 30, 90].map((days) => (
                                    <Button
                                        key={`days-${days}`}
                                        size="small"
                                        variant={internalOverviewDays === days ? 'contained' : 'outlined'}
                                        onClick={() => handleInternalOverviewDaysChange(days)}
                                        sx={{
                                            borderRadius: '999px',
                                            minWidth: '84px',
                                            ...(internalOverviewDays === days
                                                ? { backgroundColor: brandRed, '&:hover': { backgroundColor: '#a30000' } }
                                                : { borderColor: '#d0d5dd', color: offBlack })
                                        }}
                                    >
                                        {days} dias
                                    </Button>
                                ))}
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => loadInternalOverview(internalOverviewDays, internalEventsPage, internalEventsPageSize, internalEventsFilters)}
                                    sx={{ borderRadius: '999px', px: 2.2 }}
                                >
                                    Atualizar
                                </Button>
                                <Chip
                                    size="small"
                                    label={`Janela: ${internalOverviewData?.period?.days || internalOverviewDays} dias`}
                                    sx={{
                                        borderRadius: '999px',
                                        border: '1px solid #d0d5dd',
                                        color: neutralGray,
                                        backgroundColor: '#fff'
                                    }}
                                />
                                {internalOverviewData?.scope?.mode === 'executive' && (
                                    <Chip
                                        size="small"
                                        label="Visão executiva"
                                        sx={{
                                            borderRadius: '999px',
                                            border: '1px solid rgba(14,165,233,0.25)',
                                            color: '#0369a1',
                                            backgroundColor: 'rgba(14,165,233,0.08)',
                                            fontWeight: 700
                                        }}
                                    />
                                )}
                                {Number(internalOverviewData?.dataQuality?.excludedOperationalEvents || 0) > 0 && (
                                    <Chip
                                        size="small"
                                        label={`${formatInt(internalOverviewData?.dataQuality?.excludedOperationalEvents)} eventos operacionais removidos`}
                                        sx={{
                                            borderRadius: '999px',
                                            border: '1px solid rgba(249,115,22,0.22)',
                                            color: '#c2410c',
                                            backgroundColor: 'rgba(249,115,22,0.08)',
                                            fontWeight: 700
                                        }}
                                    />
                                )}
                                {internalOverviewLoading && internalOverviewData && (
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.2 }}>
                                        <CircularProgress size={16} />
                                        <Typography variant="caption" sx={{ color: neutralGray, fontWeight: 600 }}>
                                            Atualizando...
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Paper>

                    {internalOverviewError && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {internalOverviewError}
                        </Alert>
                    )}

                    {internalOverviewLoading && !internalOverviewData && (
                        <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {internalOverviewData && (
                        <Box>
                            <Box
                                sx={{
                                    mb: 3,
                                    display: 'grid',
                                    gap: 2.5,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        lg: '1.5fr minmax(300px, 1fr)'
                                    }
                                }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2.4,
                                        borderRadius: '18px',
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        background: 'linear-gradient(135deg, #1f2937 0%, #27384e 46%, #2e4159 100%)',
                                        color: '#fff'
                                    }}
                                >
                                    <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: 'rgba(255,255,255,0.78)', fontWeight: 700 }}>
                                        Tendência diária
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.4, color: '#fff' }}>
                                        Evolução de Eventos ({internalOverviewData?.period?.days || internalOverviewDays} dias)
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.8 }}>
                                        <Chip
                                            size="small"
                                            label={`Total: ${formatInt(kpis.totalEvents)}`}
                                            sx={{
                                                color: '#f8fbff',
                                                borderColor: 'rgba(148,163,184,0.55)',
                                                backgroundColor: 'rgba(148,163,184,0.18)',
                                                fontWeight: 700
                                            }}
                                            variant="outlined"
                                        />
                                        <Chip
                                            size="small"
                                            label={`Pico diário: ${formatInt(maxDayValue)}`}
                                            sx={{
                                                color: '#f8fbff',
                                                borderColor: 'rgba(148,163,184,0.55)',
                                                backgroundColor: 'rgba(148,163,184,0.18)',
                                                fontWeight: 700
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, eventsByDay.length)}, minmax(0, 1fr))`, gap: 0.7, alignItems: 'end', height: 110 }}>
                                        {eventsByDay.map((point) => {
                                            const value = Number(point?.value || 0);
                                            const heightPct = Math.max(10, Math.round((value / maxDayValue) * 100));
                                            return (
                                                <Box key={`day-${point.day}`} sx={{ minWidth: 0, height: '100%', display: 'flex', alignItems: 'flex-end' }}>
                                                    <Tooltip
                                                        arrow
                                                        placement="top"
                                                        title={`${point.day}: ${formatInt(value)} eventos`}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: '100%',
                                                                height: `${heightPct}%`,
                                                                borderRadius: '8px 8px 3px 3px',
                                                                background: 'linear-gradient(180deg, #60a5fa 0%, #22d3ee 100%)',
                                                                boxShadow: '0 8px 18px rgba(34, 211, 238, 0.25)',
                                                                transition: 'transform 0.18s ease, filter 0.18s ease',
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    transform: 'translateY(-2px)',
                                                                    filter: 'brightness(1.06)'
                                                                }
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, eventsByDay.length)}, minmax(0, 1fr))`, gap: 0.6, mt: 0.7 }}>
                                        {eventsByDay.map((point, index) => (
                                            <Typography
                                                key={`day-label-${point.day}`}
                                                sx={{
                                                    fontSize: '0.63rem',
                                                    color: 'rgba(219, 234, 254, 0.95)',
                                                    fontWeight: 600,
                                                    textAlign: 'center',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                {index % dayLabelStep === 0 ? String(point.day || '').slice(5) : ' '}
                                            </Typography>
                                        ))}
                                    </Box>
                                </Paper>

                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2.3,
                                        borderRadius: '18px',
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        backgroundColor: '#fff'
                                    }}
                                >
                                    <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: neutralGray }}>
                                        Eficiência de conversão
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: offBlack, mb: 1.4 }}>
                                        Indicadores de Funil
                                    </Typography>

                                    <Box
                                        sx={{
                                            mb: 1.6,
                                            display: 'grid',
                                            gap: 1,
                                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
                                        }}
                                    >
                                        <Paper elevation={0} sx={{ p: 1.25, borderRadius: '14px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                            <Typography variant="caption" sx={{ color: neutralGray, fontWeight: 700, display: 'block' }}>
                                                Início de preenchimento
                                            </Typography>
                                            <Typography variant="h6" sx={{ color: offBlack, fontWeight: 800, lineHeight: 1.1 }}>
                                                {formatPercent(funnel.viewToStartRate)}
                                            </Typography>
                                        </Paper>
                                        <Paper elevation={0} sx={{ p: 1.25, borderRadius: '14px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                            <Typography variant="caption" sx={{ color: neutralGray, fontWeight: 700, display: 'block' }}>
                                                Intento de envio
                                            </Typography>
                                            <Typography variant="h6" sx={{ color: offBlack, fontWeight: 800, lineHeight: 1.1 }}>
                                                {formatPercent(funnel.startToIntentRate)}
                                            </Typography>
                                        </Paper>
                                    </Box>

                                    <Box sx={{ mb: 1.7 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.45 }}>
                                            <Typography variant="body2" sx={{ color: neutralGray }}>Conversão Lead Form</Typography>
                                            <Typography variant="body2" sx={{ color: offBlack, fontWeight: 700 }}>{formatPercent(leadConversionRate)}</Typography>
                                        </Box>
                                        <Box sx={{ height: 10, borderRadius: '999px', backgroundColor: '#e8edf3', overflow: 'hidden' }}>
                                            <Box sx={{ width: `${Math.min(100, leadConversionRate)}%`, height: '100%', background: 'linear-gradient(90deg,#ef4444,#f97316)' }} />
                                        </Box>
                                    </Box>

                                    <Box sx={{ mb: 1.7 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.45 }}>
                                            <Typography variant="body2" sx={{ color: neutralGray }}>Validação antes do envio</Typography>
                                            <Typography variant="body2" sx={{ color: offBlack, fontWeight: 700 }}>{formatPercent(funnel.validationShareRate)}</Typography>
                                        </Box>
                                        <Box sx={{ height: 10, borderRadius: '999px', backgroundColor: '#e8edf3', overflow: 'hidden' }}>
                                            <Box sx={{ width: `${Math.min(100, funnel.validationShareRate || 0)}%`, height: '100%', background: 'linear-gradient(90deg,#f97316,#fb7185)' }} />
                                        </Box>
                                    </Box>

                                    <Box sx={{ mb: 1.7 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.45 }}>
                                            <Typography variant="body2" sx={{ color: neutralGray }}>WhatsApp por Lead</Typography>
                                            <Typography variant="body2" sx={{ color: offBlack, fontWeight: 700 }}>{formatPercent(whatsappShare)}</Typography>
                                        </Box>
                                        <Box sx={{ height: 10, borderRadius: '999px', backgroundColor: '#e8edf3', overflow: 'hidden' }}>
                                            <Box sx={{ width: `${Math.min(100, whatsappShare)}%`, height: '100%', background: 'linear-gradient(90deg,#0ea5e9,#22c55e)' }} />
                                        </Box>
                                    </Box>

                                    <Divider sx={{ my: 1.4 }} />
                                    <Typography variant="caption" sx={{ color: neutralGray }}>
                                        Atualizado para janela de {internalOverviewData?.period?.days || internalOverviewDays} dias.
                                    </Typography>
                                </Paper>
                            </Box>

                            <Box
                                sx={{
                                    mb: 3,
                                    display: 'grid',
                                    gap: 2.5,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        sm: 'repeat(2, minmax(0, 1fr))',
                                        md: 'repeat(3, minmax(0, 1fr))',
                                        lg: 'repeat(4, minmax(0, 1fr))'
                                    }
                                }}
                            >
                                {internalKpiCards.map((item) => (
                                    <Paper
                                        key={item.label}
                                        elevation={0}
                                        sx={{
                                            p: 1.7,
                                            minHeight: 92,
                                            borderRadius: '18px',
                                            border: '1px solid rgba(15,23,42,0.08)',
                                            backgroundColor: '#fff',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 12px 24px rgba(15,23,42,0.09)'
                                            },
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                left: 0,
                                                top: 0,
                                                width: '100%',
                                                height: '4px',
                                                background: item.accent
                                            }
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: neutralGray,
                                                fontWeight: 700,
                                                letterSpacing: '0.045em',
                                                display: 'block',
                                                minHeight: 22
                                            }}
                                        >
                                            {item.label}
                                        </Typography>
                                        <Typography variant="h5" sx={{ mt: 0.7, color: offBlack, fontWeight: 800, lineHeight: 1.1 }}>
                                            {formatInt(item.value)}
                                        </Typography>
                                    </Paper>
                                ))}
                            </Box>

                            <Box
                                sx={{
                                    mb: 3,
                                    display: 'grid',
                                    gap: 2.5,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        lg: '1.2fr minmax(320px, 0.8fr)'
                                    }
                                }}
                            >
                                <Paper elevation={0} sx={{ p: 2.5, borderRadius: '18px', border: '1px solid rgba(0,0,0,0.08)' }}>
                                    <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: neutralGray }}>
                                        Diagnóstico de conversão
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: offBlack, mb: 1.8 }}>
                                        Funil do formulário de contato
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gap: 1.15,
                                            gridTemplateColumns: {
                                                xs: '1fr',
                                                md: 'repeat(5, minmax(0, 1fr))'
                                            },
                                            mb: 2
                                        }}
                                    >
                                        {funnelSteps.map((step) => (
                                            <Paper
                                                key={step.key}
                                                elevation={0}
                                                sx={{
                                                    p: 1.4,
                                                    borderRadius: '16px',
                                                    border: '1px solid #e5e7eb',
                                                    background: 'linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)'
                                                }}
                                            >
                                                <Box sx={{ height: 4, borderRadius: '999px', background: step.accent, mb: 1.1 }} />
                                                <Typography variant="caption" sx={{ color: neutralGray, fontWeight: 700, display: 'block', minHeight: 34 }}>
                                                    {step.label}
                                                </Typography>
                                                <Typography variant="h5" sx={{ color: offBlack, fontWeight: 800, lineHeight: 1.05, mb: 0.4 }}>
                                                    {formatInt(step.value)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#98a2b3' }}>
                                                    {step.helper}
                                                </Typography>
                                            </Paper>
                                        ))}
                                    </Box>

                                    <List dense sx={{ p: 0 }}>
                                        {funnelInsights.map((insight, index) => (
                                            <ListItemButton
                                                key={`funnel-insight-${index}`}
                                                disableRipple
                                                sx={{
                                                    mb: 0.7,
                                                    px: 1.2,
                                                    py: 0.9,
                                                    borderRadius: '14px',
                                                    alignItems: 'flex-start',
                                                    backgroundColor: '#f8fafc',
                                                    border: '1px solid #e2e8f0',
                                                    '&:hover': { backgroundColor: '#f8fafc' }
                                                }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 30, mt: 0.15 }}>
                                                    <PlayCircleOutlineIcon sx={{ fontSize: 18, color: brandRed }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={insight}
                                                    primaryTypographyProps={{
                                                        variant: 'body2',
                                                        sx: { color: offBlack, lineHeight: 1.5 }
                                                    }}
                                                />
                                            </ListItemButton>
                                        ))}
                                    </List>
                                </Paper>

                                <Paper elevation={0} sx={{ p: 2.5, borderRadius: '18px', border: '1px solid rgba(0,0,0,0.08)' }}>
                                    <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: neutralGray }}>
                                        Atrito observado
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: offBlack, mb: 1.8 }}>
                                        Páginas e fricções
                                    </Typography>

                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: offBlack, mb: 1 }}>
                                        Páginas com maior volume de lead
                                    </Typography>
                                    <Stack spacing={0.8} sx={{ mb: 2.1 }}>
                                        {funnelTopPages.length === 0 && (
                                            <Typography variant="body2" sx={{ color: neutralGray }}>
                                                Aguardando amostra de funil enriquecida.
                                            </Typography>
                                        )}
                                        {funnelTopPages.map((item) => (
                                            <Box key={`lead-page-${item.label}`} sx={{ py: 0.2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.4 }}>
                                                    <Typography variant="body2" sx={{ color: neutralGray, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {item.rank}. {item.label}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: offBlack, fontWeight: 700 }}>
                                                        {formatInt(item.views)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ height: 8, borderRadius: '999px', backgroundColor: '#eef2f6', overflow: 'hidden' }}>
                                                    <Box sx={{ width: item.width, height: '100%', background: 'linear-gradient(90deg,#2563eb,#38bdf8)' }} />
                                                </Box>
                                                <Typography variant="caption" sx={{ color: '#98a2b3' }}>
                                                    Starts {formatInt(item.starts)} • Intentos {formatInt(item.submitIntents)} • Envios {formatInt(item.submitSuccess)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>

                                    <Divider sx={{ my: 1.6 }} />

                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: offBlack, mb: 1 }}>
                                        Campos com mais validação
                                    </Typography>
                                    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mb: 1.8 }}>
                                        {topValidationFields.length === 0 && (
                                            <Typography variant="body2" sx={{ color: neutralGray }}>
                                                Ainda sem erros de validação no período.
                                            </Typography>
                                        )}
                                        {topValidationFields.map((item) => (
                                            <Chip
                                                key={`validation-${item.label}`}
                                                label={`${item.label}: ${formatInt(item.numericValue)}`}
                                                sx={{ borderRadius: '999px', backgroundColor: 'rgba(249,115,22,0.12)', color: '#c2410c', fontWeight: 700 }}
                                            />
                                        ))}
                                    </Stack>

                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: offBlack, mb: 1 }}>
                                        Motivos de erro técnico
                                    </Typography>
                                    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                                        {topSubmitErrorReasons.length === 0 && (
                                            <Typography variant="body2" sx={{ color: neutralGray }}>
                                                Nenhum erro técnico de envio registrado nesta janela.
                                            </Typography>
                                        )}
                                        {topSubmitErrorReasons.map((item) => (
                                            <Chip
                                                key={`submit-error-${item.label}`}
                                                label={`${item.label}: ${formatInt(item.numericValue)}`}
                                                sx={{ borderRadius: '999px', backgroundColor: 'rgba(239,68,68,0.10)', color: '#b42318', fontWeight: 700, maxWidth: '100%' }}
                                            />
                                        ))}
                                    </Stack>
                                </Paper>
                            </Box>

                            <Box
                                sx={{
                                    mb: 3,
                                    display: 'grid',
                                    gap: 2.5,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        lg: 'repeat(3, minmax(0, 1fr))'
                                    }
                                }}
                            >
                                <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.08)', height: '100%', minHeight: 320 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: offBlack }}>
                                            Top Origens
                                        </Typography>
                                        <Stack spacing={0.2}>
                                        {topSources.length === 0 && (
                                            <Typography variant="body2" sx={{ color: neutralGray }}>
                                                Sem dados no período selecionado.
                                            </Typography>
                                        )}
                                        {topSources.map((item) => (
                                            <Box key={`src-${item.label}`} sx={{ py: 0.9 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.45, gap: 1 }}>
                                                    <Typography variant="body2" sx={{ color: neutralGray, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {item.rank}. {item.label}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: offBlack }}>{formatInt(item.numericValue)}</Typography>
                                                </Box>
                                                <Box sx={{ height: 8, borderRadius: '999px', backgroundColor: '#eef2f6', overflow: 'hidden' }}>
                                                    <Box sx={{ width: item.width, height: '100%', background: 'linear-gradient(90deg,#0ea5e9,#22d3ee)' }} />
                                                </Box>
                                            </Box>
                                        ))}
                                        </Stack>
                                    </Paper>
                                <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.08)', height: '100%', minHeight: 320 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: offBlack }}>
                                            Top UFs
                                        </Typography>
                                        <Stack spacing={0.2}>
                                        {topStates.length === 0 && (
                                            <Typography variant="body2" sx={{ color: neutralGray }}>
                                                Sem dados no período selecionado.
                                            </Typography>
                                        )}
                                        {topStates.map((item) => (
                                            <Box key={`state-${item.label}`} sx={{ py: 0.9 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.45, gap: 1 }}>
                                                    <Typography variant="body2" sx={{ color: neutralGray }}>{item.rank}. {item.label}</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: offBlack }}>{formatInt(item.numericValue)}</Typography>
                                                </Box>
                                                <Box sx={{ height: 8, borderRadius: '999px', backgroundColor: '#eef2f6', overflow: 'hidden' }}>
                                                    <Box sx={{ width: item.width, height: '100%', background: 'linear-gradient(90deg,#ef4444,#fb7185)' }} />
                                                </Box>
                                            </Box>
                                        ))}
                                        </Stack>
                                    </Paper>
                                <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.08)', height: '100%', minHeight: 320 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: offBlack }}>
                                            Top Páginas
                                        </Typography>
                                        <Stack spacing={0.2}>
                                        {topPages.length === 0 && (
                                            <Typography variant="body2" sx={{ color: neutralGray }}>
                                                Sem dados no período selecionado.
                                            </Typography>
                                        )}
                                        {topPages.map((item) => (
                                            <Box key={`page-${item.label}`} sx={{ py: 0.9 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.45, gap: 1.5 }}>
                                                    <Typography variant="body2" sx={{ color: neutralGray, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {item.rank}. {item.label}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: offBlack }}>{formatInt(item.numericValue)}</Typography>
                                                </Box>
                                                <Box sx={{ height: 8, borderRadius: '999px', backgroundColor: '#eef2f6', overflow: 'hidden' }}>
                                                    <Box sx={{ width: item.width, height: '100%', background: 'linear-gradient(90deg,#6366f1,#a855f7)' }} />
                                                </Box>
                                            </Box>
                                        ))}
                                        </Stack>
                                    </Paper>
                            </Box>

                            <Box
                                sx={{
                                    mb: 3,
                                    display: 'grid',
                                    gap: 2.5,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        lg: 'minmax(0, 1fr) minmax(0, 1.2fr)'
                                    }
                                }}
                            >
                                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.08)', height: '100%', minHeight: 360 }}>
                                        <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: neutralGray }}>
                                            Relacionamento
                                        </Typography>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: offBlack }}>
                                            Últimos Contatos
                                        </Typography>
                                        {(internalOverviewData.recentContacts || []).length === 0 ? (
                                            <Typography variant="body2" sx={{ color: neutralGray }}>
                                                Nenhuma mensagem registrada na janela selecionada.
                                            </Typography>
                                        ) : (
                                            <Stack spacing={1.4} sx={{ maxHeight: 330, overflowY: 'auto', pr: 0.5 }}>
                                                {(internalOverviewData.recentContacts || []).map((contact) => (
                                                    <Box key={`contact-${contact.id}`} sx={{ p: 1.35, borderRadius: '12px', border: '1px solid #eef2f6', backgroundColor: '#fafbfc' }}>
                                                        <Typography variant="body2" sx={{ color: offBlack, fontWeight: 700 }}>
                                                            {contact.name}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: neutralGray, display: 'block' }}>
                                                            {contact.email} {contact.phone ? `• ${contact.phone}` : ''}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: neutralGray }}>
                                                            {formatDateTime(contact.createdAt)}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        )}
                                    </Paper>
                                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.08)', height: '100%', minHeight: 360 }}>
                                        <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: neutralGray }}>
                                            Engajamento
                                        </Typography>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: offBlack }}>
                                            Eventos por Tipo
                                        </Typography>
                                        <Stack spacing={1.1}>
                                            {(internalOverviewData.topEvents || []).map((event) => {
                                                const largest = Math.max(1, ...(internalOverviewData.topEvents || []).map((e) => Number(e.value || 0)));
                                                const width = `${Math.max(8, Math.round((Number(event.value || 0) / largest) * 100))}%`;
                                                return (
                                                    <Box key={`event-type-${event.label}`}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.45 }}>
                                                            <Typography variant="body2" sx={{ color: neutralGray }}>{event.label}</Typography>
                                                            <Typography variant="body2" sx={{ color: offBlack, fontWeight: 700 }}>{formatInt(event.value)}</Typography>
                                                        </Box>
                                                        <Box sx={{ height: 8, borderRadius: '999px', backgroundColor: '#eef2f6', overflow: 'hidden' }}>
                                                            <Box sx={{ width, height: '100%', background: 'linear-gradient(90deg,#0ea5e9,#22c55e)' }} />
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </Paper>
                            </Box>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    borderRadius: '18px',
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    backgroundColor: '#fff',
                                    overflowX: 'auto'
                                }}
                            >
                                <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: neutralGray }}>
                                    Auditoria
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: offBlack }}>
                                    Eventos Recentes
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gap: 1.2,
                                        gridTemplateColumns: {
                                            xs: '1fr',
                                            md: 'repeat(3, minmax(0, 1fr))',
                                            lg: 'repeat(6, minmax(0, 1fr))'
                                        },
                                        mb: 2
                                    }}
                                >
                                    <TextField
                                        size="small"
                                        label="Busca"
                                        value={internalEventsSearchInput}
                                        onChange={(event) => setInternalEventsSearchInput(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') handleApplyInternalEventsSearch();
                                        }}
                                        placeholder="Página, IP, referrer..."
                                    />
                                    <TextField
                                        select
                                        size="small"
                                        label="Evento"
                                        value={internalEventsFilters.eventName}
                                        onChange={(event) => handleInternalEventsFilterChange('eventName', event.target.value)}
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        {eventFilterOptions.map((option) => (
                                            <MenuItem key={`event-filter-${option}`} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        select
                                        size="small"
                                        label="Origem"
                                        value={internalEventsFilters.source}
                                        onChange={(event) => handleInternalEventsFilterChange('source', event.target.value)}
                                    >
                                        <MenuItem value="">Todas</MenuItem>
                                        {sourceFilterOptions.map((option) => (
                                            <MenuItem key={`source-filter-${option}`} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        select
                                        size="small"
                                        label="País"
                                        value={internalEventsFilters.countryCode}
                                        onChange={(event) => handleInternalEventsFilterChange('countryCode', event.target.value)}
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        {countryFilterOptions.map((option) => (
                                            <MenuItem key={`country-filter-${option}`} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        select
                                        size="small"
                                        label="Ordenar por"
                                        value={internalEventsFilters.sortBy}
                                        onChange={(event) => handleInternalEventsFilterChange('sortBy', event.target.value)}
                                    >
                                        <MenuItem value="createdAt">Data/Hora</MenuItem>
                                        <MenuItem value="eventName">Evento</MenuItem>
                                        <MenuItem value="pagePath">Página</MenuItem>
                                        <MenuItem value="source">Origem</MenuItem>
                                        <MenuItem value="ip">IP</MenuItem>
                                        <MenuItem value="country">País</MenuItem>
                                    </TextField>
                                    <TextField
                                        select
                                        size="small"
                                        label="Direção"
                                        value={internalEventsFilters.sortDir}
                                        onChange={(event) => handleInternalEventsFilterChange('sortDir', event.target.value)}
                                    >
                                        <MenuItem value="desc">Decrescente</MenuItem>
                                        <MenuItem value="asc">Crescente</MenuItem>
                                    </TextField>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.2, flexWrap: 'wrap', mb: 2 }}>
                                    <Typography variant="caption" sx={{ color: neutralGray }}>
                                        Filtros ativos: {activeTableFiltersCount}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={handleClearInternalEventsFilters}
                                            sx={{ borderRadius: '999px' }}
                                        >
                                            Limpar filtros
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={handleApplyInternalEventsSearch}
                                            sx={{
                                                borderRadius: '999px',
                                                backgroundColor: brandRed,
                                                '&:hover': { backgroundColor: '#a30000' }
                                            }}
                                        >
                                            Aplicar busca
                                        </Button>
                                    </Box>
                                </Box>
                                {(internalOverviewData.recentEvents || []).length === 0 ? (
                                    <Typography variant="body2" sx={{ color: neutralGray }}>
                                        Sem eventos recentes para exibir na janela selecionada.
                                    </Typography>
                                ) : (
                                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                                    <Box component="thead">
                                        <Box component="tr">
                                            {['Data/Hora', 'Evento', 'Página', 'Origem', 'IP', 'Localização', 'Cookies'].map((header) => (
                                                <Box
                                                    key={header}
                                                    component="th"
                                                    sx={{
                                                        textAlign: 'left',
                                                        fontSize: '0.74rem',
                                                        color: neutralGray,
                                                        py: 1.25,
                                                        borderBottom: '1px solid #eceff3',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.04em'
                                                    }}
                                                >
                                                    {header}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                    <Box component="tbody">
                                        {(internalOverviewData.recentEvents || []).map((event) => {
                                            const cookieSummary = getCookieSnapshotSummary(event.cookieSnapshot);
                                            return (
                                            <Box component="tr" key={`event-${event.id}`}>
                                                <Box component="td" sx={{ py: 1.1, borderBottom: '1px solid #f2f4f7', color: neutralGray, fontSize: '0.84rem' }}>
                                                    {formatDateTime(event.createdAt)}
                                                </Box>
                                                <Box component="td" sx={{ py: 1.1, borderBottom: '1px solid #f2f4f7', fontSize: '0.84rem' }}>
                                                    <Box sx={{ display: 'inline-flex', px: 1.1, py: 0.3, borderRadius: '999px', fontSize: '0.74rem', fontWeight: 700, color: '#0f172a', backgroundColor: '#edf2f7' }}>
                                                        {event.eventName}
                                                    </Box>
                                                </Box>
                                                <Box component="td" sx={{ py: 1.1, borderBottom: '1px solid #f2f4f7', color: neutralGray, fontSize: '0.84rem' }}>
                                                    {event.pagePath || event.pageUrl || '-'}
                                                </Box>
                                                <Box component="td" sx={{ py: 1.1, borderBottom: '1px solid #f2f4f7', color: neutralGray, fontSize: '0.84rem' }}>
                                                    {event.utmSource || '(direct)'} / {event.utmMedium || '(none)'}
                                                </Box>
                                                <Box component="td" sx={{ py: 1.1, borderBottom: '1px solid #f2f4f7', color: neutralGray, fontSize: '0.84rem' }}>
                                                    {event.ip || '-'}
                                                </Box>
                                                <Box component="td" sx={{ py: 1.1, borderBottom: '1px solid #f2f4f7', color: neutralGray, fontSize: '0.84rem' }}>
                                                    {event.countryCode || '-'} {event.city ? `- ${event.city}` : ''}
                                                </Box>
                                                <Box component="td" sx={{ py: 1.1, borderBottom: '1px solid #f2f4f7', color: neutralGray, fontSize: '0.82rem' }}>
                                                    {cookieSummary.totalCount > 0 ? (
                                                        <Stack direction="row" spacing={0.6} alignItems="center" useFlexGap flexWrap="wrap">
                                                            <Chip
                                                                size="small"
                                                                label={`${cookieSummary.usefulCount} úteis`}
                                                                sx={{ borderRadius: '999px', fontWeight: 700, backgroundColor: 'rgba(59,130,246,0.12)', color: '#1d4ed8' }}
                                                            />
                                                            <Tooltip title={cookieSummary.namesPreview.join(', ') || 'Sem nomes disponíveis'}>
                                                                <Button
                                                                    size="small"
                                                                    variant="text"
                                                                    onClick={() => handleOpenCookieDetails(event)}
                                                                    sx={{ minWidth: 0, px: 0.8, borderRadius: '999px' }}
                                                                >
                                                                    Ver
                                                                </Button>
                                                            </Tooltip>
                                                        </Stack>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </Box>
                                            </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                                )}
                                <TablePagination
                                    component="div"
                                    count={Number(recentEventsPagination.total || 0)}
                                    page={Math.max(0, Number(recentEventsPagination.page || 1) - 1)}
                                    onPageChange={(_, nextPage) => handleInternalEventsPageChange(nextPage + 1)}
                                    rowsPerPage={Number(recentEventsPagination.pageSize || internalEventsPageSize)}
                                    onRowsPerPageChange={handleInternalEventsPageSizeChange}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    labelRowsPerPage="Eventos por página"
                                    labelDisplayedRows={({ from, to, count }) => `${formatInt(from)}-${formatInt(to)} de ${formatInt(count)}`}
                                    sx={{
                                        mt: 1.5,
                                        borderTop: '1px solid #eceff3',
                                        '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                            color: neutralGray,
                                            fontSize: '0.85rem',
                                            mb: 0
                                        }
                                    }}
                                />
                                <Dialog
                                    open={cookieDetailsDialogOpen}
                                    onClose={handleCloseCookieDetails}
                                    maxWidth="md"
                                    fullWidth
                                >
                                    <DialogTitle>
                                        Cookies Capturados do Evento
                                    </DialogTitle>
                                    <DialogContent dividers>
                                        <Typography variant="body2" sx={{ color: neutralGray, mb: 1.2 }}>
                                            Evento #{selectedCookieEvent?.id || '-'} • {selectedCookieEvent?.eventName || '-'} • {formatDateTime(selectedCookieEvent?.createdAt)}
                                        </Typography>
                                        <Box
                                            component="pre"
                                            sx={{
                                                mt: 0,
                                                mb: 0,
                                                p: 1.5,
                                                borderRadius: '12px',
                                                backgroundColor: '#0f172a',
                                                color: '#e2e8f0',
                                                fontSize: '0.75rem',
                                                overflow: 'auto',
                                                maxHeight: '50vh',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word'
                                            }}
                                        >
                                            {JSON.stringify(selectedCookieEvent?.cookieSnapshot || {}, null, 2)}
                                        </Box>
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={handleCloseCookieDetails} variant="contained" sx={{ borderRadius: '999px', backgroundColor: brandRed, '&:hover': { backgroundColor: '#a30000' } }}>
                                            Fechar
                                        </Button>
                                    </DialogActions>
                                </Dialog>
                            </Paper>
                        </Box>
                    )}
                </Box>
            )}

            {activeTab === 'seoHealth' && (
                <Box>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2.5, md: 3.5 },
                            mb: 3,
                            borderRadius: '20px',
                            border: '1px solid rgba(0,0,0,0.08)',
                            background: 'linear-gradient(130deg, #ffffff 0%, #f8fafc 42%, #f4f6f8 100%)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 2, alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' } }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: offBlack, mb: 0.7 }}>
                                    SEO Monitor
                                </Typography>
                                <Typography variant="body2" sx={{ color: neutralGray, maxWidth: '860px' }}>
                                    Diagnóstico técnico de indexação: domínio canônico, redirects, sitemap e robots.
                                </Typography>
                                <Typography variant="caption" sx={{ color: neutralGray, mt: 0.8, display: 'block' }}>
                                    Última verificação: {seoHealthData?.checkedAt ? formatDateTime(seoHealthData.checkedAt) : 'N/D'}
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={loadSeoHealthOverview}
                                disabled={seoHealthLoading}
                                sx={{
                                    borderRadius: '999px',
                                    px: 2.5,
                                    backgroundColor: brandRed,
                                    '&:hover': { backgroundColor: '#a30000' }
                                }}
                            >
                                {seoHealthLoading ? 'Verificando...' : 'Atualizar Check SEO'}
                            </Button>
                        </Box>
                    </Paper>

                    {seoHealthError && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {seoHealthError}
                        </Alert>
                    )}

                    {seoHealthLoading && (
                        <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {!seoHealthLoading && seoHealthData && (
                        <Box>
                            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                                {[
                                    { label: 'Score de Saúde', value: `${Number(seoSummary?.healthScore || 0)}%`, note: seoStatusLabel },
                                    { label: 'Checks Aprovados', value: `${Number(seoSummary?.passedChecks || 0)}/${Number(seoSummary?.totalChecks || 0)}`, note: 'validações técnicas' },
                                    { label: 'Domínio Canônico', value: seoHealthData?.config?.canonicalHost || 'N/D', note: 'host principal' },
                                    { label: 'Falhas Atuais', value: String(seoFailedChecks.length), note: 'itens com atenção' }
                                ].map((item) => (
                                    <Grid item xs={12} sm={6} md={3} key={item.label}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2.3,
                                                borderRadius: '16px',
                                                border: '1px solid rgba(0,0,0,0.08)',
                                                backgroundColor: '#fff'
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ color: neutralGray, fontWeight: 700, letterSpacing: '0.04em' }}>
                                                {item.label}
                                            </Typography>
                                            <Typography variant="h5" sx={{ mt: 1, color: offBlack, fontWeight: 800 }}>
                                                {item.value}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: item.label === 'Score de Saúde' ? seoStatusColor : neutralGray }}>
                                                {item.note}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>

                            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={4}>
                                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.08)', height: '100%' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: offBlack }}>
                                            Home Canônica
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: neutralGray, mb: 0.9 }}>
                                            URL: {seoTechnical?.home?.url || '-'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: neutralGray, mb: 0.9 }}>
                                            Canonical: {seoTechnical?.home?.canonicalHref || '-'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: neutralGray, mb: 0.9 }}>
                                            OG URL: {seoTechnical?.home?.ogUrl || '-'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: neutralGray }}>
                                            Robots: {seoTechnical?.home?.robotsMeta || '-'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.08)', height: '100%' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: offBlack }}>
                                            Sitemap
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: neutralGray, mb: 0.9 }}>
                                            URL: {seoTechnical?.sitemap?.url || '-'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: neutralGray, mb: 0.9 }}>
                                            Status HTTP: {seoTechnical?.sitemap?.statusCode || 'N/D'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: neutralGray, mb: 0.9 }}>
                                            URLs: {formatInt(seoTechnical?.sitemap?.urlCount || 0)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: neutralGray }}>
                                            Hosts: {(seoTechnical?.sitemap?.hosts || []).join(', ') || '-'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.08)', height: '100%' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: offBlack }}>
                                            robots.txt
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: neutralGray, mb: 0.9 }}>
                                            URL: {seoTechnical?.robots?.url || '-'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: neutralGray, mb: 0.9 }}>
                                            Status HTTP: {seoTechnical?.robots?.statusCode || 'N/D'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: neutralGray }}>
                                            Sitemaps: {(seoTechnical?.robots?.sitemaps || []).join(', ') || '-'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>

                            <Box
                                sx={{
                                    mb: 3,
                                    display: 'grid',
                                    gap: 2.5,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        lg: 'minmax(340px, 0.9fr) minmax(0, 1.1fr)'
                                    }
                                }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2.5,
                                        borderRadius: '18px',
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        background: 'linear-gradient(145deg,#101828 0%,#1d2939 100%)',
                                        color: '#fff'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 1.5 }}>
                                        <Box>
                                            <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: 'rgba(255,255,255,0.68)' }}>
                                                Resumo Executivo
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.8 }}>
                                                Risco SEO agora
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={seoExecutiveSummary?.seoRiskStatus === 'controlled' ? 'Controlado' : 'Em atenção'}
                                            sx={{
                                                borderRadius: '999px',
                                                backgroundColor: seoExecutiveSummary?.seoRiskStatus === 'controlled' ? 'rgba(34,197,94,0.18)' : 'rgba(249,115,22,0.18)',
                                                color: '#fff',
                                                fontWeight: 700
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.65, mb: 1.6 }}>
                                        {seoExecutiveSummary?.seoRiskHeadline || 'Acompanhe aqui a combinação entre saúde técnica, páginas estratégicas e contenção de URLs legadas.'}
                                    </Typography>
                                    <Box sx={{ display: 'grid', gap: 1.1, mb: 1.6 }}>
                                        {seoPulseSegments.map((item) => {
                                            const toneColor = item.tone === 'good' ? '#22c55e' : '#f97316';
                                            const toneBg = item.tone === 'good' ? 'rgba(34,197,94,0.14)' : 'rgba(249,115,22,0.14)';
                                            return (
                                                <Box
                                                    key={`seo-pulse-${item.key}`}
                                                    sx={{
                                                        p: 1.2,
                                                        borderRadius: '14px',
                                                        border: '1px solid rgba(255,255,255,0.10)',
                                                        backgroundColor: 'rgba(255,255,255,0.06)'
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', mb: 0.65 }}>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.62)', fontWeight: 700, display: 'block', letterSpacing: '0.04em' }}>
                                                                {item.label}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700 }}>
                                                                {item.headline}
                                                            </Typography>
                                                        </Box>
                                                        <Chip
                                                            size="small"
                                                            label={item.value}
                                                            sx={{
                                                                borderRadius: '999px',
                                                                backgroundColor: toneBg,
                                                                color: '#fff',
                                                                fontWeight: 700
                                                            }}
                                                        />
                                                    </Box>
                                                    <Box sx={{ height: 8, borderRadius: '999px', backgroundColor: 'rgba(255,255,255,0.10)', overflow: 'hidden', mb: 0.55 }}>
                                                        <Box
                                                            sx={{
                                                                width: `${item.percent}%`,
                                                                height: '100%',
                                                                borderRadius: '999px',
                                                                background: `linear-gradient(90deg, ${toneColor}, ${item.tone === 'good' ? '#86efac' : '#fdba74'})`
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.68)' }}>
                                                        {item.detail}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                    <Stack spacing={0.9}>
                                        {(seoExecutiveSummary?.recommendations || []).map((recommendation, index) => (
                                            <Box
                                                key={`seo-rec-${index}`}
                                                sx={{
                                                    p: 1.15,
                                                    borderRadius: '14px',
                                                    border: '1px solid rgba(255,255,255,0.10)',
                                                    backgroundColor: 'rgba(255,255,255,0.06)'
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.55 }}>
                                                    {recommendation}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Paper>

                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2.5,
                                        borderRadius: '18px',
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        backgroundColor: '#fff'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                        <Box>
                                            <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: neutralGray }}>
                                                Search Console
                                            </Typography>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: offBlack }}>
                                                Páginas estratégicas para acompanhar
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={`${formatInt(seoHealthData?.priorityPagesMonitor?.healthy || 0)} saudáveis`}
                                            sx={{ borderRadius: '999px', backgroundColor: 'rgba(22,163,74,0.12)', color: '#166534', fontWeight: 700 }}
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gap: 1.15,
                                            gridTemplateColumns: {
                                                xs: '1fr',
                                                md: 'repeat(2, minmax(0, 1fr))'
                                            }
                                        }}
                                    >
                                        {priorityPageMonitorItems.map((item) => (
                                            <Paper
                                                key={`priority-page-${item.path}`}
                                                elevation={0}
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: '16px',
                                                    border: '1px solid #e5e7eb',
                                                    backgroundColor: '#f8fafc'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.8, alignItems: 'center' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                                        <Box
                                                            sx={{
                                                                width: 12,
                                                                height: 12,
                                                                borderRadius: '999px',
                                                                flexShrink: 0,
                                                                backgroundColor: item.healthy ? '#16a34a' : '#f97316',
                                                                boxShadow: item.healthy ? '0 0 0 4px rgba(22,163,74,0.12)' : '0 0 0 4px rgba(249,115,22,0.12)'
                                                            }}
                                                        />
                                                        <Typography variant="body2" sx={{ color: offBlack, fontWeight: 700 }}>
                                                            {item.label}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        size="small"
                                                        label={item.healthy ? 'Indexável' : 'Atenção'}
                                                        sx={{
                                                            borderRadius: '999px',
                                                            backgroundColor: item.healthy ? 'rgba(22,163,74,0.12)' : 'rgba(249,115,22,0.14)',
                                                            color: item.healthy ? '#166534' : '#c2410c',
                                                            fontWeight: 700
                                                        }}
                                                    />
                                                </Box>
                                                <Typography variant="caption" sx={{ color: neutralGray, display: 'block', mb: 0.65 }}>
                                                    {item.path} • {formatInt(item.baselineImpressions || 0)} impressões • {formatInt(item.baselineClicks || 0)} cliques • CTR {formatPercent((Number(item.baselineCtr || 0)) / 100)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: neutralGray, display: 'block', mb: 0.7 }}>
                                                    Posição média {Number(item.baselinePosition || 0).toFixed(2)}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: offBlack, lineHeight: 1.5, mb: 0.8 }}>
                                                    {item.focus}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gap: 0.8,
                                                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }
                                                    }}
                                                >
                                                    <Box sx={{ p: 1, borderRadius: '12px', backgroundColor: '#fff', border: '1px solid #eef2f6' }}>
                                                        <Typography variant="caption" sx={{ color: neutralGray, fontWeight: 700, display: 'block', mb: 0.2 }}>
                                                            Canonical
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: offBlack, wordBreak: 'break-word' }}>
                                                            {item.canonicalHref || 'N/D'}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ p: 1, borderRadius: '12px', backgroundColor: '#fff', border: '1px solid #eef2f6' }}>
                                                        <Typography variant="caption" sx={{ color: neutralGray, fontWeight: 700, display: 'block', mb: 0.2 }}>
                                                            Robots
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: offBlack }}>
                                                            {item.robotsMeta || 'N/D'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Paper>
                                        ))}
                                    </Box>
                                </Paper>
                            </Box>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    mb: 3,
                                    borderRadius: '18px',
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    background: 'linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)'
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: { xs: 'flex-start', md: 'center' },
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        mb: 2.2,
                                        flexDirection: { xs: 'column', md: 'row' }
                                    }}
                                >
                                    <Box>
                                        <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: neutralGray }}>
                                            Search Console
                                        </Typography>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: offBlack }}>
                                            URLs legadas monitoradas
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: neutralGray, maxWidth: '760px' }}>
                                            Acompanhamento das rotas antigas que ainda aparecem em impressões orgânicas. O objetivo aqui é garantir que elas permaneçam contidas com página 404 lógica e `noindex`.
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                                        <Chip
                                            label={`${formatInt(seoHealthData?.legacyUrlsMonitor?.total || 0)} monitoradas`}
                                            sx={{ borderRadius: '999px', backgroundColor: '#eef2ff', color: '#3730a3', fontWeight: 700 }}
                                        />
                                        <Chip
                                            label={`${formatInt(seoHealthData?.legacyUrlsMonitor?.contained || 0)} contidas`}
                                            sx={{ borderRadius: '999px', backgroundColor: 'rgba(22,163,74,0.12)', color: '#166534', fontWeight: 700 }}
                                        />
                                        <Chip
                                            label={`${Math.max(0, Number(seoHealthData?.legacyUrlsMonitor?.total || 0) - Number(seoHealthData?.legacyUrlsMonitor?.contained || 0))} em atenção`}
                                            sx={{ borderRadius: '999px', backgroundColor: 'rgba(249,115,22,0.14)', color: '#c2410c', fontWeight: 700 }}
                                        />
                                    </Stack>
                                </Box>

                                {legacyUrlMonitorItems.length === 0 ? (
                                    <Typography variant="body2" sx={{ color: neutralGray }}>
                                        Nenhuma URL legada monitorada nesta checagem.
                                    </Typography>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gap: 1.2
                                        }}
                                    >
                                        {legacyUrlMonitorItems.map((item) => (
                                            <Paper
                                                key={`legacy-monitor-${item.path}`}
                                                elevation={0}
                                                sx={{
                                                    p: 1.8,
                                                    borderRadius: '16px',
                                                    border: '1px solid #e5e7eb',
                                                    backgroundColor: '#fff'
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: { xs: 'flex-start', md: 'center' },
                                                        justifyContent: 'space-between',
                                                        gap: 1.5,
                                                        mb: 1.1,
                                                        flexDirection: { xs: 'column', md: 'row' }
                                                    }}
                                                >
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography variant="body2" sx={{ color: offBlack, fontWeight: 700, wordBreak: 'break-word' }}>
                                                            {item.path}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: neutralGray }}>
                                                            Baseline Search Console: {formatInt(item.baselineImpressions || 0)} impressões • {formatInt(item.baselineClicks || 0)} cliques • posição média {Number(item.baselinePosition || 0).toFixed(2)}
                                                        </Typography>
                                                    </Box>
                                                    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                                                        <Chip
                                                            size="small"
                                                            label={item.priority === 'high' ? 'Prioridade alta' : item.priority === 'medium' ? 'Prioridade média' : 'Prioridade baixa'}
                                                            sx={{
                                                                borderRadius: '999px',
                                                                backgroundColor: item.priority === 'high' ? 'rgba(220,38,38,0.12)' : item.priority === 'medium' ? 'rgba(249,115,22,0.14)' : 'rgba(37,99,235,0.12)',
                                                                color: item.priority === 'high' ? '#991b1b' : item.priority === 'medium' ? '#c2410c' : '#1d4ed8',
                                                                fontWeight: 700
                                                            }}
                                                        />
                                                        <Chip
                                                            size="small"
                                                            label={item.mitigationActive ? 'Contida' : 'Em atenção'}
                                                            sx={{
                                                                borderRadius: '999px',
                                                                backgroundColor: item.mitigationActive ? 'rgba(22,163,74,0.12)' : 'rgba(249,115,22,0.14)',
                                                                color: item.mitigationActive ? '#166534' : '#c2410c',
                                                                fontWeight: 700
                                                            }}
                                                        />
                                                    </Stack>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gap: 1.2,
                                                        gridTemplateColumns: {
                                                            xs: '1fr',
                                                            md: 'repeat(4, minmax(0, 1fr))'
                                                        },
                                                        mb: 1.25
                                                    }}
                                                >
                                                    {[
                                                        { label: 'Status HTTP', value: item.httpStatus || 'N/D' },
                                                        { label: 'Robots', value: item.robotsMeta || 'N/D' },
                                                        { label: 'Canonical', value: item.canonicalHref || 'N/D' },
                                                        { label: 'Title', value: item.title || 'N/D' }
                                                    ].map((field) => (
                                                        <Box
                                                            key={`${item.path}-${field.label}`}
                                                            sx={{
                                                                p: 1.15,
                                                                borderRadius: '12px',
                                                                border: '1px solid #eef2f6',
                                                                backgroundColor: '#f8fafc',
                                                                minWidth: 0
                                                            }}
                                                        >
                                                            <Typography variant="caption" sx={{ color: neutralGray, fontWeight: 700, display: 'block', mb: 0.35 }}>
                                                                {field.label}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ color: offBlack, wordBreak: 'break-word' }}>
                                                                {field.value}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>

                                                <Typography variant="caption" sx={{ color: neutralGray }}>
                                                    Recomendação: {item.recommendation}
                                                </Typography>
                                            </Paper>
                                        ))}
                                    </Box>
                                )}
                            </Paper>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    borderRadius: '18px',
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    backgroundColor: '#fff',
                                    overflowX: 'auto'
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: offBlack }}>
                                    Checklist Técnico SEO
                                </Typography>
                                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                                    <Box component="thead">
                                        <Box component="tr">
                                            {['Status', 'Validação', 'Detalhe'].map((header) => (
                                                <Box
                                                    key={header}
                                                    component="th"
                                                    sx={{
                                                        textAlign: 'left',
                                                        fontSize: '0.74rem',
                                                        color: neutralGray,
                                                        py: 1.25,
                                                        borderBottom: '1px solid #eceff3',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.04em'
                                                    }}
                                                >
                                                    {header}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                    <Box component="tbody">
                                        {seoChecks.map((check) => (
                                            <Box component="tr" key={check.id}>
                                                <Box component="td" sx={{ py: 1.1, borderBottom: '1px solid #f2f4f7' }}>
                                                    <Chip
                                                        size="small"
                                                        label={check.ok ? 'OK' : 'Atenção'}
                                                        sx={{
                                                            fontWeight: 700,
                                                            backgroundColor: check.ok ? 'rgba(22,163,74,0.15)' : 'rgba(220,38,38,0.12)',
                                                            color: check.ok ? '#166534' : '#991b1b'
                                                        }}
                                                    />
                                                </Box>
                                                <Box component="td" sx={{ py: 1.1, borderBottom: '1px solid #f2f4f7', color: offBlack, fontSize: '0.86rem', fontWeight: 600 }}>
                                                    {check.label}
                                                </Box>
                                                <Box component="td" sx={{ py: 1.1, borderBottom: '1px solid #f2f4f7', color: neutralGray, fontSize: '0.84rem' }}>
                                                    {check.detail || '-'}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Paper>
                        </Box>
                    )}
                </Box>
            )}

            {activeTab === 'contactRouting' && (
                <Box>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2.3, md: 2.6 },
                            borderRadius: '20px',
                            border: '1px solid rgba(0,0,0,0.08)',
                            backgroundColor: '#fff',
                            boxShadow: '0 8px 20px rgba(15,23,42,0.04)'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', lg: 'row' },
                                justifyContent: 'space-between',
                                alignItems: { xs: 'flex-start', lg: 'flex-start' },
                                gap: 1.8,
                                mb: 2
                            }}
                        >
                            <Box sx={{ maxWidth: 760 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: offBlack, mb: 0.45 }}>
                                    Roteamento do formulário de contato
                                </Typography>
                                <Typography variant="body2" sx={{ color: neutralGray, lineHeight: 1.5 }}>
                                    Organize os destinatários de e-mail e WhatsApp em uma única grade. As alterações abaixo controlam quem recebe os contatos e por qual canal a notificação interna é distribuída.
                                </Typography>
                            </Box>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                <Chip
                                    label={`Origem: ${contactRoutingForm.source || 'N/D'}`}
                                    variant="outlined"
                                    sx={{
                                        borderRadius: '999px',
                                        borderColor: '#d9dee7',
                                        color: neutralGray,
                                        backgroundColor: '#fff',
                                        fontWeight: 600
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleSaveContactRouting}
                                    disabled={contactRoutingSaving}
                                    sx={{
                                        borderRadius: '999px',
                                        px: 3,
                                        py: 1.05,
                                        backgroundColor: brandRed,
                                        minWidth: 188,
                                        boxShadow: '0 8px 18px rgba(210, 25, 14, 0.14)',
                                        '&:hover': { backgroundColor: '#a30000' }
                                    }}
                                >
                                    {contactRoutingSaving ? 'Salvando...' : 'Salvar Roteamento'}
                                </Button>
                            </Stack>
                        </Box>

                        {contactRoutingError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {contactRoutingError}
                            </Alert>
                        )}

                        {contactRoutingLoading ? (
                            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            p: { xs: 1.6, md: 1.8 },
                                            border: '1px solid #e7ecf2',
                                            borderRadius: '16px',
                                            background: 'linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%)'
                                        }}
                                    >
                                        <Grid container spacing={1.5} alignItems="center">
                                            <Grid item xs={12} lg={7}>
                                                <TextField
                                                    fullWidth
                                                    label="Remetente (From)"
                                                    value={contactRoutingForm.from}
                                                    onChange={(e) => handleContactRoutingChange('from', e.target.value)}
                                                    placeholder="contato@r5id.com.br"
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={12} lg={5}>
                                                <Stack
                                                    direction={{ xs: 'column', sm: 'row' }}
                                                    spacing={1}
                                                    justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
                                                    alignItems={{ xs: 'stretch', sm: 'center' }}
                                                >
                                                    <Chip
                                                        label={`${(contactRoutingForm.toList || []).length} TO • ${(contactRoutingForm.bccList || []).length} BCC • ${(contactRoutingForm.whatsappToList || []).length} WhatsApp`}
                                                        sx={{
                                                            borderRadius: '999px',
                                                            backgroundColor: '#f3f5f8',
                                                            color: offBlack,
                                                            fontWeight: 700,
                                                            border: '1px solid #e1e6ee'
                                                        }}
                                                    />
                                                    <Typography variant="caption" sx={{ color: neutralGray }}>
                                                        Salve para aplicar as alterações no recebimento.
                                                    </Typography>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                                {routingChannelCards.map((card) => (
                                    <Grid item xs={12} md={4} key={card.key}>
                                        <Box sx={{ ...routingCardSx, minHeight: 248 }}>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.15 }}>
                                                {card.icon}
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: offBlack }}>
                                                    {card.title}
                                                </Typography>
                                            </Stack>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: neutralGray,
                                                    minHeight: 44,
                                                    lineHeight: 1.45,
                                                    mb: 1.15
                                                }}
                                            >
                                                {card.description}
                                            </Typography>
                                            <Stack spacing={1.05} sx={{ flexGrow: 1, justifyContent: 'space-between' }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label={card.label}
                                                    placeholder={card.placeholder}
                                                    value={contactRoutingDraft[card.key]}
                                                    onChange={(e) => handleRecipientDraftChange(card.key, e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleAddRecipients(card.key);
                                                        }
                                                    }}
                                                />
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.1 }}>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={() => handleAddRecipients(card.key)}
                                                        sx={{
                                                            ...routingAddButtonSx,
                                                            '&:hover': card.buttonHover
                                                        }}
                                                    >
                                                        Adicionar
                                                    </Button>
                                                </Box>
                                                <Box sx={routingChipWrapSx}>
                                                    {card.items.map((item) => (
                                                        <Chip
                                                            key={`${card.key}-${item}`}
                                                            label={item}
                                                            onDelete={() => handleRemoveRecipient(card.key, item)}
                                                            color="default"
                                                            variant="outlined"
                                                            sx={{
                                                                borderRadius: '999px',
                                                                backgroundColor: '#fff',
                                                                borderColor: '#d9dee7',
                                                                fontWeight: card.itemWeight
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Stack>
                                        </Box>
                                    </Grid>
                                ))}
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: { xs: 'column', md: 'row' },
                                            justifyContent: 'space-between',
                                            alignItems: { xs: 'flex-start', md: 'center' },
                                            gap: 1.5,
                                            pt: 0.2,
                                            borderTop: '1px solid #edf1f5',
                                            mt: 0.2,
                                            pt: 1.6
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ color: neutralGray }}>
                                            Dica: use TO para destinatários principais, BCC para cópia oculta e WhatsApp para alertas imediatos.
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: neutralGray }}>
                                            O salvamento aplica a configuração para todo o site.
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}
                    </Paper>
                </Box>
            )}

            {activeTab === 'links' && (
                <Box>
                    {/* GLOBAL HEADER */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 4,
                            borderRadius: '24px',
                            border: '1px solid rgba(0,0,0,0.08)',
                            backgroundColor: '#fff',
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: 3
                        }}
                    >
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: neutralGray, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Título Principal da Seção
                            </Typography>
                            <TextField
                                fullWidth
                                variant="standard"
                                value={linksForm.sectionTitle || ''}
                                onChange={(e) => setLinksForm({ ...linksForm, sectionTitle: e.target.value })}
                                placeholder="Ex: O QUE FAZEMOS"
                                InputProps={{
                                    sx: {
                                        fontSize: '1.5rem',
                                        fontWeight: 800,
                                        color: offBlack,
                                        '&:before, &:after': { display: 'none' }
                                    }
                                }}
                            />
                        </Box>
                        <Button
                            variant="outlined"
                            onClick={handleLinksUpdate}
                            startIcon={<SaveIcon />}
                            sx={{ borderRadius: '14px', whiteSpace: 'nowrap', px: 3 }}
                        >
                            Salvar Título
                        </Button>
                    </Paper>

                    {!linksForm.columns ? (
                        <Box sx={{ p: 10, textAlign: 'center' }}>
                            <CircularProgress color="primary" />
                            <Typography sx={{ mt: 2, color: neutralGray }}>Sincronizando conteúdos...</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ width: '100%' }}>
                            {/* FOLDER-STYLE TABS */}
                            <Box sx={{ px: 2 }}>
                                <Tabs
                                    value={selectedLinksCol}
                                    onChange={(e, newVal) => setSelectedLinksCol(newVal)}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{
                                        mb: -0.1, // overlap border
                                        '& .MuiTabs-indicator': { display: 'none' },
                                        '& .MuiTab-root': {
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            fontSize: '0.9rem',
                                            color: neutralGray,
                                            backgroundColor: '#f5f5f5',
                                            border: '1px solid #e0e0e0',
                                            borderBottom: 'none',
                                            borderRadius: '12px 12px 0 0',
                                            mr: 1,
                                            minHeight: '48px',
                                            transition: 'all 0.2s',
                                            opacity: 0.7,
                                            '&.Mui-selected': {
                                                color: brandRed,
                                                backgroundColor: '#fff',
                                                opacity: 1,
                                                borderColor: 'rgba(0,0,0,0.1)',
                                                borderBottom: '1px solid #fff',
                                                zIndex: 1
                                            }
                                        }
                                    }}
                                >
                                    {linksForm.columns.map((col, idx) => (
                                        <Tab
                                            key={idx}
                                            label={
                                                <Box sx={{ textAlign: 'left' }}>
                                                    <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.8 }}>COLUNA {idx + 1}</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{col.title || '(Sem Título)'}</Typography>
                                                </Box>
                                            }
                                        />
                                    ))}
                                </Tabs>
                            </Box>

                            {/* MAIN EDITOR AREA */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 2, md: 4 },
                                    borderRadius: '0 0 24px 24px',
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    backgroundColor: '#fff',
                                    mb: 10
                                }}
                            >
                                {/* COLUMN TITLE CONFIG */}
                                <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <TextField
                                        label="Título desta Coluna"
                                        size="small"
                                        fullWidth
                                        value={linksForm.columns[selectedLinksCol].title}
                                        onChange={(e) => {
                                            const newLinks = { ...linksForm };
                                            newLinks.columns[selectedLinksCol].title = e.target.value;
                                            setLinksForm(newLinks);
                                        }}
                                        sx={{ maxWidth: 400 }}
                                    />

                                </Box>

                                {/* ITEMS LIST - COMPACT DESIGN */}
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={(event) => handleDragEndLinks(event, selectedLinksCol)}
                                >
                                    <SortableContext
                                        items={linksForm.columns[selectedLinksCol].items.map(i => i.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        <Stack spacing={3}> {/* Token 24px */}
                                            {linksForm.columns[selectedLinksCol].items.map((item, itemIdx) => (
                                                <SortableItem key={item.id} id={item.id} handle={true}>
                                                    <Box sx={{
                                                        backgroundColor: '#fff',
                                                        border: '1px solid #eaecf0',
                                                        borderRadius: '16px', // Token 16px
                                                        display: 'flex',
                                                        alignItems: 'stretch',
                                                        width: '100%',
                                                        transition: 'all 0.2s',
                                                        overflow: 'hidden',
                                                        '&:hover': {
                                                            borderColor: brandRed,
                                                            boxShadow: '0 8px 24px rgba(0,0,0,0.06)' // Adjusted shadow
                                                        }
                                                    }}>
                                                        {/* CARD CONTENT */}
                                                        <Box sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                            <Box>
                                                                <Typography variant="caption" sx={{ fontWeight: 700, color: neutralGray, mb: 1, display: 'block', letterSpacing: '0.05em' }}>
                                                                    NOME DO SERVIÇO / LINK
                                                                </Typography>
                                                                <TextField
                                                                    fullWidth
                                                                    variant="outlined"
                                                                    size="small"
                                                                    value={item.label || ''}
                                                                    onChange={(e) => handleLinkItemChange(selectedLinksCol, itemIdx, 'label', e.target.value)}
                                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fcfcfc' } }}
                                                                />
                                                            </Box>

                                                            <Box>
                                                                <Typography variant="caption" sx={{ fontWeight: 700, color: neutralGray, mb: 1, display: 'block', letterSpacing: '0.05em' }}>
                                                                    DESCRIÇÃO / TOOLTIP (ALTA FIDELIDADE)
                                                                </Typography>
                                                                <TextField
                                                                    fullWidth
                                                                    multiline
                                                                    rows={4}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    value={item.description || ''}
                                                                    onChange={(e) => handleLinkItemChange(selectedLinksCol, itemIdx, 'description', e.target.value)}
                                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fcfcfc' } }}
                                                                />
                                                            </Box>
                                                        </Box>

                                                        {/* INDIVIDUAL ACTIONS BAR */}
                                                        <Box sx={{
                                                            width: '72px',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            gap: 1.5,
                                                            borderLeft: '1px solid #f0f0f0',
                                                            backgroundColor: '#f9fafb',
                                                            px: 1,
                                                            py: 2
                                                        }}>
                                                            {/* RANKING ACTIONS */}
                                                            <Stack spacing={0.5} sx={{ mb: 1, borderBottom: '1px solid #eee', pb: 1, width: '100%', alignItems: 'center' }}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleMoveLinksDirectional(selectedLinksCol, itemIdx, -1)}
                                                                    disabled={itemIdx === 0}
                                                                    sx={{ color: neutralGray, '&:hover': { color: brandRed } }}
                                                                >
                                                                    <ArrowUpwardIcon sx={{ fontSize: 18 }} />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleMoveLinksDirectional(selectedLinksCol, itemIdx, 1)}
                                                                    disabled={itemIdx === linksForm.columns[selectedLinksCol].items.length - 1}
                                                                    sx={{ color: neutralGray, '&:hover': { color: brandRed } }}
                                                                >
                                                                    <ArrowDownwardIcon sx={{ fontSize: 18 }} />
                                                                </IconButton>
                                                            </Stack>

                                                            <IconButton
                                                                size="small"
                                                                onClick={handleLinksUpdate}
                                                                disabled={saving}
                                                                sx={{
                                                                    color: neutralGray,
                                                                    backgroundColor: '#fff',
                                                                    border: '1px solid #e0e0e0',
                                                                    borderRadius: '8px',
                                                                    '&:hover': { color: '#2e7d32', borderColor: '#2e7d32', backgroundColor: '#fff' }
                                                                }}
                                                            >
                                                                {saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon fontSize="small" />}
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleRemoveLinkItem(selectedLinksCol, itemIdx)}
                                                                sx={{
                                                                    color: neutralGray,
                                                                    backgroundColor: '#fff',
                                                                    border: '1px solid #e0e0e0',
                                                                    borderRadius: '8px',
                                                                    '&:hover': { color: brandRed, borderColor: brandRed, backgroundColor: '#fff' }
                                                                }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </Box>
                                                </SortableItem>
                                            ))}

                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                onClick={() => {
                                                    const newLinks = { ...linksForm };
                                                    const newId = `new-${Date.now()}`;
                                                    newLinks.columns[selectedLinksCol].items.push({
                                                        id: newId,
                                                        label: 'Novo Serviço',
                                                        description: ''
                                                    });
                                                    setLinksForm(newLinks);
                                                }}
                                                sx={{
                                                    borderStyle: 'dashed',
                                                    py: 2, // Token 16px
                                                    borderColor: '#d0d5dd',
                                                    color: neutralGray,
                                                    borderRadius: '16px', // Token 16px
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    '&:hover': { backgroundColor: '#f9fafb', borderStyle: 'dashed', borderColor: brandRed, color: brandRed }
                                                }}
                                            >
                                                + Adicionar Item em "{linksForm.columns[selectedLinksCol].title || `Coluna ${selectedLinksCol + 1}`}"
                                            </Button>
                                        </Stack>
                                    </SortableContext>
                                </DndContext>
                            </Paper>

                        </Box>
                    )}
                </Box>
            )}
                </Box>
            </Box>
        </Container>
    );
};

export default AdminPanel;
